import axios from 'axios'
import { useAuthStore } from '../store/authStore'
import { useTaskListStore } from '../store/taskListStore'
import { saveDownload } from './packageDownload'

const centralAPI = axios.create({
  baseURL: '/api/central',
})

export const tenantAPI = axios.create({
  baseURL: '/api/tenant',
})

// Session expired event - will be listened to by App.tsx
export const SESSION_EXPIRED_EVENT = 'session-expired'

// Add auth token interceptor to central API
centralAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth-storage')
  if (token) {
    try {
      const { state } = JSON.parse(token)
      if (state?.token) {
        config.headers.Authorization = `Bearer ${state.token}`
      }
    } catch (e) {
      // Invalid token storage
    }
  }
  return config
})

// Add auth token interceptor to tenant API
tenantAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth-storage')
  if (token) {
    try {
      const { state } = JSON.parse(token)
      if (state?.token) {
        config.headers.Authorization = `Bearer ${state.token}`
      }
    } catch (e) {
      // Invalid token storage
    }
  }
  return config
})

// Setup response interceptors for session management
/** sessionStorage key of the page a session ended on (see App's return after sign-in). */
export const RETURN_AFTER_SIGN_IN_KEY = 'return-after-sign-in'

export const setupAxiosInterceptors = () => {
  const handleAuthError = (error: any) => {
    if (error.response?.status === 401) {
      // Skip session expired for login/auth endpoints - those have their own error handling
      const url = error.config?.url || ''
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/token')
      if (!isAuthEndpoint) {
        // The page the session ended on, so the next sign-in returns to it.
        try {
          window.sessionStorage.setItem(
            RETURN_AFTER_SIGN_IN_KEY, window.location.pathname + window.location.search)
        } catch {
          /* storage unavailable: the next sign-in opens the home page */
        }
        // Token expired or invalid - dispatch custom event
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, {
          detail: { message: error.response?.data?.detail || 'Session expired' }
        }))
        // Logout user
        useAuthStore.getState().logout()
      }
    }
    return Promise.reject(error)
  }

  centralAPI.interceptors.response.use(
    (response) => response,
    handleAuthError
  )

  tenantAPI.interceptors.response.use(
    (response) => response,
    handleAuthError
  )
}

// Central Backend APIs
export interface FetchAppsParams {
  search?: string
  category?: string
  /** A publisher exactly as the catalog's filter values list it. */
  publisher?: string
  /** 'x64', 'x86' or 'arm64'. */
  architecture?: string
  /** An ISO 8601 instant: only packages published to the catalog at or after it. */
  published_since?: string
  /** Package content mode filter: 'embedded' (installer bundled) or 'dynamic' (vendor download at deploy). */
  package_mode?: string
  limit?: number
  offset?: number
}

export interface FetchAppsResponse {
  apps: any[]
  total: number
}

/** The distinct values the catalog's filter controls offer, read across the whole catalog. */
export interface CatalogFilterValues {
  categories: string[]
  publishers: string[]
}

export const fetchPublicApps = async (params?: FetchAppsParams): Promise<FetchAppsResponse> => {
  // Central expresses the mode filter as packaging_mode (embedded == bundled server-side).
  const { package_mode, ...rest } = params || {}
  const { data } = await centralAPI.get('/apps/', {
    params: { ...rest, ...(package_mode ? { packaging_mode: package_mode } : {}) },
  })
  return { apps: data.apps || [], total: data.total || data.apps?.length || 0 }
}

export const fetchPublicAppFilters = async (): Promise<CatalogFilterValues> => {
  const { data } = await centralAPI.get('/apps/filters')
  return { categories: data.categories || [], publishers: data.publishers || [] }
}

export const fetchAppDetails = async (appId: string) => {
  const { data } = await centralAPI.get(`/apps/${appId}`)
  return data
}

// Tenant Backend APIs
export const fetchTenantApps = async (params?: FetchAppsParams): Promise<FetchAppsResponse> => {
  const { data } = await tenantAPI.get('/apps', { params })
  return { apps: data.apps || [], total: data.total || data.apps?.length || 0 }
}

export const fetchTenantAppFilters = async (): Promise<CatalogFilterValues> => {
  const { data } = await tenantAPI.get('/apps/filters')
  return { categories: data.categories || [], publishers: data.publishers || [] }
}

export const fetchTenantAppDetails = async (appId: string) => {
  const { data } = await tenantAPI.get(`/apps/${appId}`)
  return data
}

// Package file browser
export interface PackageFile {
  path: string
  name: string
  size: number
  extension: string
  viewable: boolean
}

export interface PackageFilesResponse {
  app_id: string
  files: PackageFile[]
}

export interface PackageFileContent {
  path: string
  name: string
  size: number
  content: string
}

export const fetchPackageFiles = async (appId: string): Promise<PackageFilesResponse> => {
  const { data } = await tenantAPI.get(`/apps/${appId}/package-files`)
  return data
}

/** Downloads an app's package; a large one comes from a short-lived link (see packageDownload). */
export const downloadAppPackage = async (
  appId: string,
  method: string,
  versionId: string | null | undefined,
  fileName: string,
): Promise<void> => {
  const { data, headers } = await tenantAPI.get(`/apps/${appId}/download-package`, {
    params: versionId ? { method, version_id: versionId, link: 1 } : { method, link: 1 },
    responseType: 'blob'
  })
  await saveDownload(data, headers?.['content-type'], fileName)
}

export const fetchPackageFileContent = async (appId: string, path: string): Promise<PackageFileContent> => {
  const { data } = await tenantAPI.get(`/apps/${appId}/package-files/content`, { params: { path } })
  return data
}

export interface AppUpdateData {
  display_name?: string
  description?: string
  publisher?: string
  category?: string
  tags?: string[]
  version?: string
  architecture?: string
  visibility?: string
}

export const updateTenantApp = async (appId: string, updateData: AppUpdateData) => {
  const { data } = await tenantAPI.patch(`/apps/${appId}`, updateData)
  return data
}

export const publishAppPackage = async (formData: FormData) => {
  const { data } = await tenantAPI.post('/publishing/publish/package', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const publishAppPSADT = async (formData: FormData) => {
  const { data } = await tenantAPI.post('/publishing/publish/psadt', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const publishAppIntune = async (formData: FormData) => {
  const { data} = await tenantAPI.post('/publishing/publish/intune', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

// Install experience types for Intune publishing
export type InstallContext = 'system' | 'user'
export type DeviceRestartBehavior = 'allow' | 'basedOnReturnCode' | 'suppress' | 'force'

export const analyzeApplication = async (params: any) => {
  const { data } = await tenantAPI.post('/publishing/analyze', params)
  return data
}

// ============================================================================
// Auth APIs (Multi-Tenant)
// ============================================================================

import type { LoginResponse } from '../store/authStore'
import type { EndpointsToManage } from '../utils/endpointsToManage'
import type { PasswordPolicy } from '../utils/passwordPolicy'

/**
 * Login with email and password
 * POST /api/tenant/auth/login
 *
 * An account with multi-factor authentication enabled gets no token here — the answer is a
 * challenge the caller finishes with `loginMFA`.
 */
export interface MFAChallenge {
  mfa_required: true
  mfa_token: string
  expires_in: number
}

/**
 * The organization requires multi-factor authentication and this account has not enrolled: the
 * password was right, and the sign-in continues by enrolling with `loginMFASetup` and
 * `loginMFAEnable`, which also finishes the sign-in.
 */
export interface MFAEnrollment {
  mfa_enrollment_required: true
  mfa_token: string
  expires_in: number
}

/**
 * The password was right but no longer meets the organization's rules: the sign-in continues
 * by choosing a new one with `loginPasswordChange`, which answers like `login` does.
 */
export interface PasswordChangeHold {
  password_change_required: true
  password_token: string
  expires_in: number
  password_policy: PasswordPolicy
  /** The rules the current password misses, in the server's words. */
  unmet: string[]
}

export type LoginOutcome = LoginResponse | MFAChallenge | MFAEnrollment | PasswordChangeHold

export const isMFAChallenge = (r: LoginOutcome): r is MFAChallenge =>
  (r as MFAChallenge).mfa_required === true

export const isMFAEnrollment = (r: LoginOutcome): r is MFAEnrollment =>
  (r as MFAEnrollment).mfa_enrollment_required === true

export const isPasswordChangeRequired = (r: LoginOutcome): r is PasswordChangeHold =>
  (r as PasswordChangeHold).password_change_required === true

/**
 * Replace a password the organization's rules refuse, and continue the sign-in.
 * POST /api/tenant/auth/login/password
 */
export const loginPasswordChange = async (passwordToken: string, newPassword: string): Promise<LoginOutcome> => {
  const { data } = await tenantAPI.post('/auth/login/password', {
    password_token: passwordToken,
    new_password: newPassword,
  })
  return data
}

export const login = async (email: string, password: string): Promise<LoginOutcome> => {
  const { data } = await tenantAPI.post('/auth/login', {
    email,
    password,
  })
  return data
}

/**
 * Start enrolling at sign-in. POST /api/tenant/auth/login/mfa/setup
 * Mints the secret the authenticator app scans; nothing changes until `loginMFAEnable`.
 */
export const loginMFASetup = async (mfaToken: string): Promise<{
  secret: string
  otpauth_uri: string
  qr_data_uri: string | null
}> => {
  const { data } = await tenantAPI.post('/auth/login/mfa/setup', { mfa_token: mfaToken })
  return data
}

/**
 * Finish enrolling and sign in. POST /api/tenant/auth/login/mfa/enable
 * The recovery codes come back once, with the session.
 */
export const loginMFAEnable = async (
  mfaToken: string,
  code: string,
): Promise<LoginResponse & { recovery_codes: string[] }> => {
  const { data } = await tenantAPI.post('/auth/login/mfa/enable', { mfa_token: mfaToken, code })
  return data
}

/**
 * Finish a sign-in held at the MFA challenge, with a code from the authenticator app or one of
 * the account's recovery codes.
 * POST /api/tenant/auth/login/mfa
 */
export const loginMFA = async (mfaToken: string, code: string): Promise<LoginResponse> => {
  const { data } = await tenantAPI.post('/auth/login/mfa', { mfa_token: mfaToken, code })
  return data
}

/**
 * Self-service trial signup (public — no auth).
 * POST /api/tenant/auth/signup
 * Returns a login-shaped payload so the client can authenticate immediately.
 */
/**
 * The HubSpot trial form's hidden inputs and consent, as the sign-up page fills them. Keys are
 * the HubSpot form field names; the backend submits them to the form after the sign-up.
 */
export interface HubSpotTrialForm {
  latest_utm_source?: string
  latest_utm_medium?: string
  latest_utm_term?: string
  latest_utm_device?: string
  latest_utm_content?: string
  latest_utm_campaign?: string
  latest_abtest?: string
  latest_form_page?: string
  latest_marketing_page?: string
  marketing_page__c?: string
  gclid?: string
  lqid?: string
  platform?: string
  /** ISO 639-1 language for the trial nurture emails. */
  locale__c?: string
  /** The page's region hidden input (the website script's country grouping). The backend
   *  ignores it: the form and its stack__c are the environment's, not the visitor's. */
  region?: 'global' | 'eu' | 'au'
  /** true when the marketing checkbox was ticked, or consent is implicit for the country. */
  marketing_consent_check: boolean
  /** The HubSpot tracking cookie (hubspotutk) and the page the submission came from. */
  hutk?: string
  pageName?: string
  pageUri?: string
}
export interface SignupPayload {
  email: string
  full_name: string
  company_name: string
  tenant_type?: string
  /** The sign-up link's marketing parameters as one query string (spid_trial_src_ref__c), for HubSpot and the Salesforce lead. */
  trial_source_ref?: string
  /** Visitor's country, ISO 3166-1 alpha-2, for the Salesforce lead. */
  country_code?: string
  /** The "How many endpoints do you manage?" answer, for the HubSpot form and the Salesforce lead. */
  endpoints_to_manage?: EndpointsToManage
  hubspot_form?: HubSpotTrialForm
}
/** The login-shaped answer an activated sign-up gives, so the client can authenticate at once. */
export type SignupResponse = LoginResponse & { message: string; tenant: any; subscription: any }

/**
 * Request a sign-up: the backend mails an activation link to the address.
 * POST /api/tenant/auth/signup -> 202 for every address, so nothing says whether it is known.
 * `plan` is the plan the activated account will land on, when the backend says.
 */
export const signup = async (payload: SignupPayload): Promise<{ message: string; plan?: 'free' | 'trial' }> => {
  const { data } = await tenantAPI.post('/auth/signup', payload)
  return data
}

/**
 * Whether an activation link is still usable, and whose it is.
 * GET /api/tenant/auth/signup/activate/{token}
 */
export const validateActivationToken = async (token: string): Promise<{
  valid: boolean
  email?: string
  full_name?: string
}> => {
  const { data } = await tenantAPI.get(`/auth/signup/activate/${token}`)
  return data
}

/**
 * Activate a sign-up: set the password and open the account, signed in.
 * POST /api/tenant/auth/signup/activate
 */
export const activateSignup = async (token: string, password: string): Promise<SignupResponse> => {
  const { data } = await tenantAPI.post('/auth/signup/activate', { token, password })
  return data
}

/**
 * OAuth2-compatible login (form-based)
 * POST /api/tenant/auth/token
 */
export const loginOAuth2 = async (username: string, password: string): Promise<LoginResponse> => {
  const formData = new URLSearchParams()
  formData.append('username', username)
  formData.append('password', password)

  const { data } = await tenantAPI.post('/auth/token', formData)
  return data
}

/**
 * Get current user info and tenant context
 * GET /api/tenant/auth/me
 */
export const getCurrentUser = async (): Promise<{
  user: LoginResponse['user']
  tenant_context: LoginResponse['tenant_context']
  accessible_tenants: LoginResponse['accessible_tenants']
}> => {
  const { data } = await tenantAPI.get('/auth/me')
  return data
}

/**
 * Switch to a different tenant
 * POST /api/tenant/auth/switch-tenant
 */
export const switchTenant = async (tenantId: string): Promise<LoginResponse> => {
  const { data } = await tenantAPI.post('/auth/switch-tenant', {
    tenant_id: tenantId,
  })
  return data
}

/**
 * Refresh access token
 * POST /api/tenant/auth/refresh
 */
export const refreshToken = async (): Promise<{
  access_token: string
  token_type: string
  expires_in: number
}> => {
  const { data } = await tenantAPI.post('/auth/refresh')
  return data
}

/**
 * Logout (server-side logging)
 * POST /api/tenant/auth/logout
 */
export const logout = async (): Promise<{ message: string }> => {
  const { data } = await tenantAPI.post('/auth/logout')
  return data
}

/**
 * Validate a password reset token
 * GET /api/tenant/auth/reset-password/validate/{token}
 */
export const validateResetToken = async (token: string): Promise<{
  valid: boolean
  email: string | null
  expires_at: string | null
  /** The rules the new password must meet; absent on an invalid token. */
  password_policy?: PasswordPolicy | null
}> => {
  const { data } = await tenantAPI.get(`/auth/reset-password/validate/${token}`)
  return data
}

/**
 * Set password using a reset token
 * POST /api/tenant/auth/reset-password/set
 */
export const setPassword = async (token: string, password: string): Promise<{ message: string }> => {
  const { data } = await tenantAPI.post('/auth/reset-password/set', { token, password })
  return data
}

/**
 * Change password for the currently authenticated user
 * POST /api/tenant/auth/change-password
 */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
  const { data } = await tenantAPI.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  })
  return data
}

// ---------------------------------------------------------------------------
// Account — what the signed-in user owns about themselves: their profile, their preferences,
// their sign-ins, and their second factor. Everything here acts on the caller, never on a user
// picked by id; that is what /users is for and it is admin-only.
// ---------------------------------------------------------------------------

export interface AccountPreferences {
  language: string
  timezone: string
}

/** PATCH /api/tenant/auth/account/profile */
export const updateProfile = async (fullName: string): Promise<{
  id: string
  email: string
  full_name: string
  avatar_url: string | null
}> => {
  const { data } = await tenantAPI.patch('/auth/account/profile', { full_name: fullName })
  return data
}

/** GET /api/tenant/auth/account/preferences */
export const getAccountPreferences = async (): Promise<{
  preferences: AccountPreferences
  supported_languages: string[]
}> => {
  const { data } = await tenantAPI.get('/auth/account/preferences')
  return data
}

/** PATCH /api/tenant/auth/account/preferences — send only what changed. */
export const updateAccountPreferences = async (
  patch: Partial<AccountPreferences>,
): Promise<{ preferences: AccountPreferences }> => {
  const { data } = await tenantAPI.patch('/auth/account/preferences', patch)
  return data
}

export interface AccountSession {
  id: string
  created_at: string | null
  last_seen_at: string | null
  expires_at: string | null
  ip_address: string | null
  user_agent: string | null
  sign_in_method: string | null
  is_current: boolean
}

/** GET /api/tenant/auth/account/sessions */
export const listAccountSessions = async (): Promise<{ sessions: AccountSession[] }> => {
  const { data } = await tenantAPI.get('/auth/account/sessions')
  return data
}

/**
 * DELETE /api/tenant/auth/account/sessions/others
 * Ends every sign-in but this one. The other tokens stop working on their next request.
 */
export const signOutOtherSessions = async (): Promise<{ revoked: number }> => {
  const { data } = await tenantAPI.delete('/auth/account/sessions/others')
  return data
}

/** DELETE /api/tenant/auth/account/sessions/{id} */
export const revokeAccountSession = async (sessionId: string): Promise<{ message: string }> => {
  const { data } = await tenantAPI.delete(`/auth/account/sessions/${sessionId}`)
  return data
}

export interface MFAStatus {
  enabled: boolean
  confirmed_at: string | null
  recovery_codes_remaining: number
  /** False for SSO-only accounts: there is no password sign-in for a second factor to guard. */
  available: boolean
  /** An organization the user belongs to requires MFA: it cannot be turned off here. */
  required: boolean
}

/** GET /api/tenant/auth/mfa/status */
export const getMFAStatus = async (): Promise<MFAStatus> => {
  const { data } = await tenantAPI.get('/auth/mfa/status')
  return data
}

/**
 * POST /api/tenant/auth/mfa/setup
 * Mints the secret to enrol. Sign-in is unchanged until `enableMFA` proves the app has it.
 */
export const setupMFA = async (): Promise<{
  secret: string
  otpauth_uri: string
  qr_data_uri: string | null
}> => {
  const { data } = await tenantAPI.post('/auth/mfa/setup')
  return data
}

/** POST /api/tenant/auth/mfa/enable — returns the recovery codes, which are shown once. */
export const enableMFA = async (code: string): Promise<{ enabled: boolean; recovery_codes: string[] }> => {
  const { data } = await tenantAPI.post('/auth/mfa/enable', { code })
  return data
}

/** POST /api/tenant/auth/mfa/disable */
export const disableMFA = async (password: string): Promise<{ enabled: boolean }> => {
  const { data } = await tenantAPI.post('/auth/mfa/disable', { password })
  return data
}

/** POST /api/tenant/auth/mfa/recovery-codes — replaces the set; the old codes stop working. */
export const regenerateRecoveryCodes = async (password: string): Promise<{ recovery_codes: string[] }> => {
  const { data } = await tenantAPI.post('/auth/mfa/recovery-codes', { password })
  return data
}

/**
 * The password rules the signed-in user's next password must meet.
 * GET /api/tenant/auth/account/password-policy
 */
export const getPasswordPolicy = async (): Promise<PasswordPolicy> => {
  const { data } = await tenantAPI.get('/auth/account/password-policy')
  return data
}

// ============================================================================
// Tenant security policy (Settings > Security; administrators and owners)
// ============================================================================

/** The policy as the Security page edits it: every field is sent back on save. */
export interface SecurityPolicy {
  password_min_length: number
  password_require_uppercase: boolean
  password_require_lowercase: boolean
  password_require_digit: boolean
  password_require_symbol: boolean
  /** Password sign-ins only; SSO sign-ins leave the second factor to the identity provider. */
  mfa_required: boolean
  /** Minutes from the sign-in, not from the last request. */
  session_lifetime_minutes: number
}

export interface SecurityPolicyResponse {
  /** False until the page has been saved once; the policy then holds the platform defaults. */
  configured: boolean
  policy: SecurityPolicy
  limits: {
    password_min_length: { min: number; max: number }
    session_lifetime_minutes: { min: number; max: number }
  }
  password_rules: string[]
  /** Active members who sign in with a password and have not enrolled an authenticator app. */
  users_without_mfa: number
  updated_at: string | null
  updated_by: string | null
}

/** GET /api/tenant/security-policy */
export const getSecurityPolicy = async (): Promise<SecurityPolicyResponse> => {
  const { data } = await tenantAPI.get('/security-policy')
  return data
}

/** PUT /api/tenant/security-policy */
export const updateSecurityPolicy = async (policy: SecurityPolicy): Promise<SecurityPolicyResponse> => {
  const { data } = await tenantAPI.put('/security-policy', policy)
  return data
}

/**
 * Clear a member's multi-factor authentication so they enrol again; signs them out everywhere.
 * POST /api/tenant/users/{user_id}/mfa/reset
 */
export const resetUserMFA = async (userId: string): Promise<{
  user_id: string
  mfa_enabled: false
  was_enabled: boolean
  sessions_revoked: number
}> => {
  const { data } = await tenantAPI.post(`/users/${userId}/mfa/reset`)
  return data
}

// SSO Configuration APIs (admin)
export const getSSOConfig = async () => {
  const { data } = await axios.get('/api/sso/config', {
    headers: { Authorization: `Bearer ${_getToken()}` }
  })
  return data
}

export const createSSOConfig = async (config: {
  protocol: string
  idp_hint?: string
  display_name?: string
  // OIDC
  issuer_url?: string
  client_id?: string
  client_secret?: string
  oidc_scopes?: string
  // SAML
  idp_entity_id?: string
  idp_sso_url?: string
  idp_certificate?: string
  sp_entity_id?: string
  idp_metadata_url?: string
  // Common
  domain_mappings: string[]
  enforcement_mode?: string
  jit_enabled?: boolean
  jit_default_role?: string
}) => {
  const { data } = await axios.post('/api/sso/config', config, {
    headers: { Authorization: `Bearer ${_getToken()}` }
  })
  return data
}

export const updateSSOConfig = async (config: Record<string, any>) => {
  const { data } = await axios.put('/api/sso/config', config, {
    headers: { Authorization: `Bearer ${_getToken()}` }
  })
  return data
}

export const deleteSSOConfig = async () => {
  const { data } = await axios.delete('/api/sso/config', {
    headers: { Authorization: `Bearer ${_getToken()}` }
  })
  return data
}

export const testSSOConnection = async () => {
  const { data } = await axios.post('/api/sso/config/test', {}, {
    headers: { Authorization: `Bearer ${_getToken()}` }
  })
  return data
}

// Express (Microsoft) SSO APIs (admin)
export const expressSSOConnect = async (confirmSwitch = false) => {
  const { data } = await axios.post('/api/sso/express/connect', { confirm_switch: confirmSwitch }, {
    headers: { Authorization: `Bearer ${_getToken()}` }
  })
  return data as { auth_url: string; state: string }
}

export const expressSSOAdminConsent = async () => {
  const { data } = await axios.post('/api/sso/express/admin-consent', {}, {
    headers: { Authorization: `Bearer ${_getToken()}` }
  })
  return data as { consent_url: string }
}

export const expressSSODisconnect = async () => {
  const { data } = await axios.post('/api/sso/express/disconnect', {}, {
    headers: { Authorization: `Bearer ${_getToken()}` }
  })
  return data
}

// SSO Login Flow APIs (public)
export const checkSSODomain = async (email: string) => {
  const { data } = await axios.get('/api/auth/sso/check-domain', {
    params: { email }
  })
  return data as {
    sso_available: boolean
    sso_required: boolean
    protocol: string | null
    display_name: string | null
    tenant_name: string | null
    sso_mode: string | null
    idp_hint: string | null
  }
}

export const initiateSSOLogin = async (email: string) => {
  const { data } = await axios.post('/api/auth/sso/initiate', { email })
  return data as {
    auth_url: string
    state: string
    protocol: string
    display_name: string | null
    tenant_name: string
  }
}

export const handleSSOCallback = async (code: string, state: string) => {
  const { data } = await axios.post('/api/auth/sso/callback', { code, state })
  return data
}

export const claimSSOToken = async (claimCode: string) => {
  const { data } = await axios.post('/api/auth/sso/claim', { claim_code: claimCode })
  return data
}

// Helper to extract token from localStorage
function _getToken(): string {
  try {
    const stored = localStorage.getItem('auth-storage')
    if (stored) {
      const { state } = JSON.parse(stored)
      return state?.token || ''
    }
  } catch {}
  return ''
}

// Integration APIs
export const getIntuneConfig = async () => {
  const { data } = await tenantAPI.get('/integrations/intune')
  return data
}

export const configureIntune = async (config: {
  azure_ad_tenant_id: string
  client_id: string
  client_secret: string
  authority?: string
}) => {
  const { data } = await tenantAPI.post('/integrations/intune', config)
  return data
}

export const updateIntuneConfig = async (config: {
  azure_ad_tenant_id: string
  client_id: string
  client_secret: string
  authority?: string
}) => {
  const { data } = await tenantAPI.put('/integrations/intune', config)
  return data
}

export const deleteIntuneConfig = async () => {
  const { data } = await tenantAPI.delete('/integrations/intune')
  return data
}

export const testIntuneConnection = async () => {
  const { data } = await tenantAPI.post('/integrations/intune/test')
  return data
}

// The Graph application permissions the integration runs on — shown before consent
export const getIntunePermissions = async () => {
  const { data } = await tenantAPI.get('/integrations/intune/permissions')
  return data
}

// Express Registration (Consent Flow)
export const getIntuneConsentUrl = async () => {
  const { data } = await tenantAPI.post('/integrations/intune/consent-url')
  return data
}

// Unified Intune status (checks both Express and Advanced)
export const getIntuneStatus = async () => {
  const { data } = await tenantAPI.get('/integrations/intune/status')
  return data
}

// Disconnect Intune (works for both Express and Advanced)
export const disconnectIntune = async () => {
  const { data } = await tenantAPI.delete('/integrations/intune/disconnect')
  return data
}

// Health check for Express Registration
export const intuneHealthCheck = async () => {
  const { data } = await tenantAPI.post('/integrations/intune/health-check')
  return data
}

// MSP Intune Batch Onboarding
export const getManagedTenantsIntuneStatus = async () => {
  const { data } = await tenantAPI.get('/msp/managed-tenants/intune-status')
  return data
}

export const setManagedTenantAzureId = async (tenantId: string, azureTenantId: string) => {
  const { data } = await tenantAPI.put(`/msp/managed-tenants/${tenantId}/azure-tenant-id`, {
    azure_tenant_id: azureTenantId,
  })
  return data
}

export const getManagedTenantConsentUrl = async (tenantId: string) => {
  const { data } = await tenantAPI.post(`/msp/managed-tenants/${tenantId}/consent-url`)
  return data
}

// Delete a tenant app
export const deleteTenantApp = async (appId: string): Promise<{ message: string; app_id: string }> => {
  const { data } = await tenantAPI.delete(`/apps/${appId}`)
  return data
}

// Check if app exists in app store
export const checkAppExists = async (appName: string): Promise<{
  exists: boolean
  app?: {
    id: string
    name: string
    current_version: string
  }
}> => {
  const { data } = await tenantAPI.get('/apps/check-exists', {
    params: { app_name: appName }
  })
  return data
}

// Publishing APIs
export const publishExistingToIntune = async (params: {
  app_id: string
  app_name: string
  app_description: string
  version: string
  install_command: string
  uninstall_command: string
  tenant_id?: string
  // 013-psadt4-interactive-packaging: apply a deployment-experience template
  // (defer/schedule/branding) at publish. Baked immutably into this deployment.
  template_id?: string
  // Deploy-time overrides JSON (install/uninstall/detection/scope), Advanced options.
  overrides?: string
}) => {
  const formData = new FormData()
  formData.append('app_id', params.app_id)
  formData.append('app_name', params.app_name)
  formData.append('app_description', params.app_description)
  formData.append('version', params.version)
  formData.append('install_command', params.install_command)
  formData.append('uninstall_command', params.uninstall_command)
  if (params.tenant_id) {
    formData.append('tenant_id', params.tenant_id)
  }
  if (params.template_id) {
    formData.append('template_id', params.template_id)
  }
  if (params.overrides) {
    formData.append('overrides', params.overrides)
  }

  const { data } = await tenantAPI.post('/publishing/publish/intune/existing', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

/**
 * Which deployment of a package a deploy creates or updates. A package may have several
 * deployments, each with its own Intune apps, assignments and install context. `uid: null` on
 * `update` is the package's first deployment; `name` is required on `new` once the package has a
 * deployment. Omitted: the first deployment is updated, or published when there is none.
 */
export type DeploymentTarget =
  | { action: 'update'; uid: string | null; name: null }
  | { action: 'new'; uid: null; name: string | null }

// ── Bulk deploy: publish + assign many apps (private + public) with a shared experience ──
export interface BulkDeployPayload {
  // install_context per app: runAsAccount has to match what THAT installer does, so a value
  // shared across a mixed batch would publish some apps in a context they cannot honour.
  // Omitted = fall back to the batch value, then to what the package says.
  apps: {
    app_id: string
    kind?: 'private' | 'public'
    install_context?: 'system' | 'user'
    deployment?: DeploymentTarget
  }[]
  template_id?: string
  // The Intune DELIVERY template (016-template-model) — a DIFFERENT object from template_id,
  // which is the end-user experience. When set, the template's assignments REPLACE
  // intent/target/targets below, and it also supplies the App-information metadata, the
  // pattern-rendered display name, generated groups and the automated-update policy.
  intune_template_id?: string
  inline_config?: any
  // Overrides welcome.promptCondition on the chosen saved template (deployment MODE: smooth ->
  // ifRunning, interactive -> always). Ignored when inline_config is used (it already carries it).
  prompt_condition?: string
  intent?: 'required' | 'available' | 'uninstall'
  target?: 'allDevices' | 'allUsers'
  // Full targeting: one assignment per entry {type: group|allDevices|allUsers, id?, name?, include}.
  targets?: { type: 'group' | 'allDevices' | 'allUsers'; id?: string; name?: string; include: boolean }[]
  install_context?: 'system' | 'user'
  device_restart_behavior?: string
  schedule?: { available_datetime?: string; deadline_datetime?: string; use_local_time?: boolean }
  overrides?: string
  // Supersede the app's prior Intune deployment so devices auto-update. Default true.
  supersede?: boolean
  supersedence_type?: 'update' | 'replace'
  // Drop every edit made with the Edit buttons on the deployment page, so the templates' values
  // apply even where the template is kept.
  discard_edits?: boolean
  // Preflight only: add `changes` to each in-place item. Costs two assignment reads per app.
  include_changes?: boolean
}

export interface BulkDeployResult {
  total: number
  succeeded: number
  failed: number
  results: { app_id: string; name?: string; status: 'ok' | 'error'; intune_app_id?: string; detail?: string }[]
}

/** Bulk-publish + assign apps to Intune. POST /publishing/publish/intune/bulk */
/**
 * A deployment run as a JOB: recorded, handed to a worker, and resumable.
 *
 * WHY NOT PUBLISH IN THE REQUEST. Publishing one application to Intune is not one Graph call — it
 * creates the app, creates a content version and file, then POLLS for an Azure storage URI (up to
 * five minutes), uploads the content in blocks, waits for the blob to finalise, commits, polls the
 * commit, patches the app and posts assignments. A bulk of a dozen cannot survive an HTTP request:
 * a gateway timeout, a closed tab or a backend restart loses it half-finished, in the customer's
 * tenant, with nothing recording which applications got through.
 *
 * The job records every item before any of them runs, so a re-run skips what succeeded instead of
 * publishing it twice.
 */
export interface DeploymentJobItem {
  id: string
  position: number
  type: 'app' | 'branding'
  ref_id: string
  name?: string | null
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'skipped' | 'needs_attention'
  /** Present once the application exists in Intune. This is what makes a retry safe. */
  intune_app_id?: string | null
  error?: string | null
  /** What a succeeded item did, and what the publish could not do. */
  outcome?: { mode: 'published' | 'updated_in_place'; warnings: string[] } | null
  attempts: number
  finished_at?: string | null
}

/** What starting a deployment does to one app: see preflightDeploymentJob. */
export interface DeploymentPreflightItem {
  app_id: string
  name: string | null
  /** The Intune display name the chosen delivery template renders for this app. */
  rendered_name?: string | null
  /** update_in_place: the app's current version is deployed and unchanged, so the templates are
   *  applied to that Intune app. publish: a new app object, for `reason`. refused: nothing is
   *  started, for `reason`. */
  mode: 'update_in_place' | 'publish' | 'refused'
  reason:
    | 'not_deployed'
    | 'removal_incomplete'
    | 'new_version'
    | 'package_changed'
    | 'app_missing'
    | 'name_taken'
    | 'install_context'
    | 'skip_install'
    | 'new_deployment'
    | 'choose_deployment'
    | 'deployment_not_found'
    | 'deployment_name_required'
    | 'deployment_name_invalid'
    | 'deployment_name_taken'
    | 'deployment_name_reserved'
    | 'deployment_name_too_long'
    | 'deployment_name_clash'
    | null
  /** The deployment this item creates or updates; null for the package's first deployment and
   *  for a new deployment, which gets its uid when it is published. */
  deployment_uid?: string | null
  /** The target deployment's name, or the requested one for a new deployment. */
  deployment_name?: string | null
  /** The update-only app's Intune name. */
  rendered_update_name?: string | null
  /** Every group the publish creates. */
  generated_groups?: string[]
  /** A new deployment whose templates give another deployment's names: its name is added to the
   *  end of every app and group name. */
  deployment_name_added?: boolean
  /** The names this publish renders that another deployment of the package has too. */
  name_clash?: NameClash[]
  /** Groups this deployment assigns that another deployment of the same package also assigns. */
  shared_groups?: SharedGroup[]
  /** The deployed app, for an in-place item: its Intune app id, display name, publish time and
   *  the install context it was published with. */
  intune_app_id?: string | null
  display_name?: string | null
  published_at?: string | null
  install_context?: 'system' | 'user' | null
  /** Whether the deployed app is the update-only app (deployed with Skip install on). */
  skip_install?: boolean
  /** Which app object stands for the deployment. */
  role?: 'install' | 'update_only'
  /** What an in-place update leaves as it is: an app object the template does not publish. */
  notices: ('update_only_not_removed' | 'install_not_removed')[]
  /** App objects the template publishes and the deployment lacks: published and added. */
  creates?: ('install' | 'update_only')[]
  /** App objects the deployment has and the template does not publish: left as they are. */
  keeps?: ('install' | 'update_only')[]
  /** The app that already carries the rendered name, when the item is refused for it. */
  taken_by?: {
    intune_app_id: string
    display_name: string | null
    /** The package whose deployment holds the app. */
    deployment_name?: string | null
    /** The other deployment of the same package that holds the app; null is its first one. */
    other_deployment_name?: string | null
  } | null
  /** What an in-place update changes against Intune now, when the preflight was asked with
   *  `include_changes`. Null when it was not asked, the app is not in Intune, or the request had
   *  too many items (`changes_skipped`). */
  changes?: DeploymentChanges | null
  changes_skipped?: 'too_many' | null
  /** Why the changes could not be listed, when computing them failed. */
  changes_error?: string | null
}

/** A group assigned by this deployment and by another deployment of the same package. */
export interface SharedGroup {
  group: string
  group_id: string | null
  role: AppObjectRole
  intent: 'required' | 'available' | 'uninstall'
  other_deployment_uid: string | null
  /** Null for the package's first deployment. */
  other_deployment_name: string | null
  other_role: AppObjectRole
  other_intent: 'required' | 'available' | 'uninstall'
}

/** A section of a deployment that the Edit buttons on the deployment page change. */
export type OverrideSection = 'experience' | 'app_information' | 'advanced' | 'assignments'

/** When and by whom a section was edited on the deployment page. */
export interface OverrideStamp {
  at: string | null
  by: string | null
}

/**
 * An edit made on the deployment page that an in-place update keeps or replaces, on one app
 * object. `automated_updates` is the update policy (update wait, versions kept), which goes with
 * the deployment template; its `fields` are the values edited.
 */
export interface OverrideChange {
  role: AppObjectRole
  intune_app_id: string
  section: OverrideSection | 'automated_updates'
  fields?: Record<string, any>
}

/** The app objects of one deployment: the install app and the update-only app. */
export type AppObjectRole = 'install' | 'update_only'

/** A value before and after the in-place update. */
export interface BeforeAfter<T = Record<string, any>> {
  from: T | null
  to: T | null
}

/** One entry per app object the deployment has, keyed by its role. */
export type PerAppObject<T> = Partial<Record<AppObjectRole, T>>

/**
 * What an in-place update changes in the existing deployment, computed by the same function the
 * worker runs.
 */
export interface DeploymentChanges {
  /** Present for an app object whose name changes. */
  rename?: PerAppObject<{ from: string; to: string }>
  /** The user experience configuration (the template's authoring shape). */
  experience?: PerAppObject<BeforeAfter>
  /** Company Portal properties as Graph names them: owner, developer, notes, informationUrl, ... */
  app_information?: PerAppObject<BeforeAfter>
  /** Requirements, return codes and restart behaviour. */
  advanced?: PerAppObject<BeforeAfter>
  /** Assignments as display labels, e.g. "Required: All devices". */
  assignments?: PerAppObject<{
    from: string[] | null
    to: string[] | null
    added: string[]
    removed: string[]
  }>
  /** The automated-update policy in effect now and the one in effect after the update. */
  automated_updates?: {
    from: IntuneAutomatedUpdatesSnapshot | null
    to: IntuneAutomatedUpdatesSnapshot | null
  } | null
  /** Sections edited on the deployment page that the update keeps, and those it replaces with
   *  the template's values. */
  overrides?: { kept: OverrideChange[]; dropped: OverrideChange[] } | null
  /** A new version is published: `overrides` lists the page edits it keeps and drops. */
  new_version?: boolean
  /** A new version drops some of the edits made on the deployment page. */
  new_version_drops_edits?: boolean
  /** What the update will leave as it is, e.g. assignments naming a group deleted in Entra. */
  warnings?: string[]
}

/**
 * What starting this deployment does to each app, before anything is published. Reads only.
 * POST /deployments/jobs/preflight
 *
 * The same decision the worker makes, so a confirmation built from it says what the job then
 * does. `intune_readable` is false when Intune could not be read: the name check and whether a
 * deployed app still exists are then not judged.
 */
export const preflightDeploymentJob = async (payload: BulkDeployPayload): Promise<{
  intune_readable: boolean
  items: DeploymentPreflightItem[]
}> => {
  const { data } = await tenantAPI.post('/deployments/jobs/preflight', payload)
  return data
}

/** The template a deployment uses now, for one template kind. */
export interface CurrentTemplateSetting {
  /** The template applied as-is; null when the deployment was adjusted. */
  template_id: string | null
  name: string | null
  /** Whether the template (or, when adjusted, the one the Adjust started from) still exists. */
  exists: boolean
  adjusted: boolean
  /** The template an Adjust started from. */
  adjusted_from: string | null
  /** The configuration the deployment carries; null for a record written before it was kept. */
  config: any | null
  /** The template's name when the deployment was published; `name` falls back to it once the
   *  template is deleted. */
  recorded_name?: string | null
}

export interface NameClash {
  name: string
  kind: 'app' | 'update_app' | 'group'
  other_deployment_uid: string | null
  /** Null for the package's first deployment. */
  other_deployment_name: string | null
}

export interface CurrentDeploymentSettings {
  /** Null for the package's first deployment. */
  deployment_uid: string | null
  /** Null for the package's first deployment, which has no name. */
  deployment_name?: string | null
  /** The deployment's name is added to the end of every app and group name. */
  deployment_name_added?: boolean
  /** 'UpdateOnly': its update-only app's groups render {Intent} as that literal. */
  update_group_intent?: 'UpdateOnly' | null
  ux: CurrentTemplateSetting | null
  delivery: CurrentTemplateSetting | null
  /** Each Intune app object of the deployment and the sections edited on the deployment page. */
  objects: {
    role: AppObjectRole
    intune_app_id: string | null
    /** The sections edited on the deployment page, keyed by section. */
    edited: Partial<Record<OverrideSection, OverrideStamp>>
  }[]
  /** The update policy edited on the deployment page (soak_days, keep_versions, ...), if any. */
  automated_updates_overrides?: Record<string, any> | null
  /** The install context the deployment was first published with; it cannot change. */
  install_context?: 'system' | 'user' | null
}

export interface AppCurrentSettings {
  app_id: string
  /** Empty when the app has no deployment; the first deployment comes first. */
  deployments: CurrentDeploymentSettings[]
  /** A deployment job is still publishing this package: its deployment is not recorded yet. */
  publishing?: boolean
}

/**
 * The settings each app's deployment uses now, read from the deployment records only, so the
 * deploy wizard can open on them before any template is chosen.
 * POST /deployments/jobs/current-settings
 */
export const fetchCurrentSettings = async (
  appIds: string[],
): Promise<{ apps: AppCurrentSettings[] }> => {
  const { data } = await tenantAPI.post('/deployments/jobs/current-settings', {
    apps: appIds.map((app_id) => ({ app_id })),
  })
  return { apps: Array.isArray(data?.apps) ? data.apps : [] }
}

export interface DeploymentJob {
  id: string
  /** `cancelled`: an automatic update that was no longer needed when it ran. */
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled'
  error?: string | null
  /** Queued by automatic updates rather than by a person: the deployment and the versions. */
  automatic?: {
    deployment_uid: string | null
    deployment_name: string | null
    from_version: string | null
    to_version: string | null
  } | null
  total: number
  succeeded: number
  failed: number
  /** Separate from `failed`: whether its application exists is UNKNOWN, so retrying is what creates
   *  the duplicate. It has to be looked at rather than retried. */
  needs_attention: number
  items: DeploymentJobItem[]
  created_at?: string | null
  started_at?: string | null
  finished_at?: string | null
}

/** Record the deployment and hand it to a worker. Returns the JOB, not the outcome. */
export const startDeploymentJob = async (
  payload: BulkDeployPayload,
  brandingDeploymentId?: string | null,
): Promise<DeploymentJob> => {
  const { data } = await tenantAPI.post('/deployments/jobs', payload, {
    // A branding DEPLOYMENT id, not a branding template id: the worker resolves it against the
    // frozen deployment, which is the thing that was signed.
    params: brandingDeploymentId ? { branding_deployment_id: brandingDeploymentId } : undefined,
  })
  // The header's task list shows the run at once rather than on its next poll.
  useTaskListStore.getState().noteDeploymentStarted(data)
  return data
}

export const getDeploymentJob = async (id: string): Promise<DeploymentJob> => {
  const { data } = await tenantAPI.get(`/deployments/jobs/${id}`)
  return data
}

/** Every deployment run, newest first, each carrying its per-item outcome.
 *
 *  The list is what makes a partial failure visible: a job reports `completed` when it finished
 *  running, which is not the same as everything having published. */
export const listDeploymentJobs = async (): Promise<{ deployments: DeploymentJob[] }> => {
  const { data } = await tenantAPI.get('/deployments/jobs')
  return data
}

/** Re-run what did not succeed. Items that already published are skipped, not republished. */
export const retryDeploymentJob = async (id: string): Promise<DeploymentJob> => {
  const { data } = await tenantAPI.post(`/deployments/jobs/${id}/retry`)
  return data
}

const JOB_TERMINAL = ['completed', 'failed']

/** Raised by awaitDeploymentJob when the session that started the job has ended. The job itself
 *  keeps running on the worker; only the following stops, so callers report nothing. */
export class DeploymentFollowEnded extends Error {
  constructor() {
    super('The session that started this deployment has ended.')
    this.name = 'DeploymentFollowEnded'
  }
}

const currentSessionKey = (): string | null => {
  const { token, user, tenantContext } = useAuthStore.getState()
  return token ? `${user?.id ?? ''}|${tenantContext?.tenant_id ?? ''}` : null
}

/** A finished job read as the outcome the callers already handle. */
const jobResult = (job: DeploymentJob): BulkDeployResult => {
  const apps = job.items.filter((i) => i.type === 'app')
  return {
    total: apps.length,
    succeeded: apps.filter((i) => i.status === 'succeeded').length,
    // needs_attention counts as not-succeeded here: whether it published is unknown, and reporting
    // it as success would tell an admin the application is out when it may not be.
    failed: apps.filter((i) => i.status !== 'succeeded').length,
    results: apps.map((i) => ({
      app_id: i.ref_id,
      name: i.name ?? undefined,
      status: i.status === 'succeeded' ? ('ok' as const) : ('error' as const),
      intune_app_id: i.intune_app_id ?? undefined,
      detail:
        i.error ??
        (i.status === 'needs_attention'
          ? 'This may or may not have published. Check Intune before retrying — retrying an item ' +
            'that already published creates a second application.'
          : i.status === 'skipped'
            ? 'Skipped because the branding it was deployed with did not go out.'
            : undefined),
    })),
  }
}

/**
 * Follow a job that is already running to its end.
 *
 * Separate from starting it because the two have different owners: the caller that starts a
 * deployment can be gone — a dialog that closes as soon as the job is recorded — while the job
 * itself still has minutes to run and an outcome that must still be reported.
 *
 * Polled rather than waited on: a deployment that takes minutes must not hold a connection open
 * to be observed.
 */
export const awaitDeploymentJob = async (
  jobId: string,
  onProgress?: (job: DeploymentJob) => void,
): Promise<BulkDeployResult> => {
  const session = currentSessionKey()
  let job = await getDeploymentJob(jobId)
  onProgress?.(job)

  while (!JOB_TERMINAL.includes(job.status)) {
    await new Promise((r) => setTimeout(r, 2000))
    // The job belongs to the user and tenant that started it. Once they sign out or switch
    // tenant, polling would only collect a 401 or 404 and report it to whoever is signed in now.
    if (currentSessionKey() !== session) throw new DeploymentFollowEnded()
    job = await getDeploymentJob(jobId)
    onProgress?.(job)
  }

  return jobResult(job)
}

/**
 * Run a deployment as a job and resolve when it finishes, in the shape the callers already handle.
 *
 * The mapping is deliberate: every existing success and error surface keeps working, so switching
 * to the job is not also a rewrite of what an admin sees. `onProgress` is offered for callers that
 * want to show items landing one by one — the whole reason the work is a job.
 */
export const deployViaJob = async (
  payload: BulkDeployPayload,
  opts: { brandingDeploymentId?: string | null; onProgress?: (job: DeploymentJob) => void } = {},
): Promise<BulkDeployResult> => {
  const job = await startDeploymentJob(payload, opts.brandingDeploymentId)
  opts.onProgress?.(job)
  if (JOB_TERMINAL.includes(job.status)) return jobResult(job)
  return awaitDeploymentJob(job.id, opts.onProgress)
}

export const bulkPublishToIntune = async (payload: BulkDeployPayload): Promise<BulkDeployResult> => {
  const { data } = await tenantAPI.post('/publishing/publish/intune/bulk', payload)
  return data
}

// ── Deploy-time override baseline (Advanced options on publish) ──
export interface RecommendedConfig {
  install_args: string
  uninstall_command: string
  detection: any
  install_scope: string
  /** Why the packager decided that scope. Shown beside the selector so an override is an
   *  informed one rather than a guess. */
  install_scope_reasons?: string[]
  source: string
}

export const getRecommendedConfig = async (appId: string): Promise<{ app_id: string; baseline: RecommendedConfig }> => {
  const { data } = await tenantAPI.get(`/publishing/recommended-config/${appId}`)
  return data
}

// ── Deployment templates + branding (013-psadt4-interactive-packaging) ──
/** The tenant's deployments that use a template, each labelled "<package> · <deployment>". */
export interface TemplateUsage {
  count: number
  deployments: string[]
}

export interface DeploymentTemplate {
  id: string
  name: string
  description?: string | null
  /** This tenant's choice: the template the deploy wizard opens on. */
  is_default: boolean
  /** The built-in default a tenant that has chosen nothing deploys with. Only a global template. */
  built_in_default: boolean
  branding_id?: string | null
  scope: 'global' | 'tenant'
  config_json: any
  schema_version: number
  updated_at?: string | null
  used_by?: TemplateUsage
}

export const listDeploymentTemplates = async (): Promise<{ templates: DeploymentTemplate[] }> => {
  const { data } = await tenantAPI.get('/templates')
  return data
}

export const getDeploymentTemplate = async (id: string): Promise<DeploymentTemplate> => {
  const { data } = await tenantAPI.get(`/templates/${id}`)
  return data
}

export const createDeploymentTemplate = async (body: {
  name: string
  description?: string
  config_json: any
  branding_id?: string
  is_default?: boolean
}): Promise<DeploymentTemplate> => {
  const { data } = await tenantAPI.post('/templates', body)
  return data
}

export const updateDeploymentTemplate = async (id: string, body: {
  name?: string
  description?: string
  config_json?: any
  branding_id?: string
  is_default?: boolean
}): Promise<DeploymentTemplate> => {
  const { data } = await tenantAPI.put(`/templates/${id}`, body)
  return data
}

export const deleteDeploymentTemplate = async (id: string): Promise<void> => {
  await tenantAPI.delete(`/templates/${id}`)
}

/** Copy a template into one this tenant owns and may edit.
 *
 * The only route out of a vendor-shipped baseline: those are read-only and the API refuses to
 * edit or delete them. The server derives the name and drops default/read-only standing, so the
 * caller does not have to know what must not be copied. */
export const duplicateDeploymentTemplate = async (id: string): Promise<DeploymentTemplate> => {
  const { data } = await tenantAPI.post(`/templates/${id}/duplicate`)
  return data
}

// ---------------------------------------------------------------------------
// Intune deployment templates (016-template-model §6)
//
// A DIFFERENT object from DeploymentTemplate above. That one is the UX template:
// what the end user sees, baked onto the install command. This one is delivery —
// who gets the app and how, materialised as the Graph app object plus its
// assignments. The design spec calls THIS "deployment template"; the older code
// calls the other one the same thing, so the names are unavoidably close.
// ---------------------------------------------------------------------------
export interface IntuneAppMetadata {
  notes: string
  owner: string
  developer: string
  categories: string[]
  featured: boolean
  informationUrl: string
  privacyUrl: string
}

export interface IntuneNamingPatterns {
  appPattern: string
  updateAppPattern: string
  groupPattern: string
  updateGroupPattern: string
}

export interface IntuneAutomatedUpdates {
  followNewVersions: boolean
  soakDays: number
  keepVersions: number
  supersede: boolean
  uninstallPrevious: boolean
  /** Read-only: always true, and the API REJECTS it on write ("it is the reason to create a
   *  supersedence at all"). Optional here because the wizard's save payload must omit it. */
  autoUpdateFromCompanyPortal?: boolean
}

export interface IntuneDeploymentTemplateConfig {
  intuneMetadata: IntuneAppMetadata
  naming: IntuneNamingPatterns
  appAssignments: any[]
  updateOnly: { enabled: boolean; matchUnmanaged?: boolean; assignments: any[] }
  /** No install app object is created; the update-only object is the only one published. */
  skipInstall?: boolean
  automatedUpdates: IntuneAutomatedUpdates
  allowUninstallFromCompanyPortal: boolean
  /** spec 6.7 and 6.4. All three are sent by the wizard and stored by the backend; the type
   *  omitted them, which compiled only because the call site is not an object literal — so the
   *  one place that would have caught a dropped field could not. */
  requirements?: any
  restartBehaviour?: 'suppress' | 'basedOnReturnCode' | 'force' | 'allow'
  returnCodes?: { returnCode: number; type: string }[]
}

export interface IntuneDeploymentTemplate {
  id: string
  name: string
  description?: string | null
  /** This tenant's choice: the template the deploy wizard opens on and a publish inherits. */
  is_default: boolean
  /** The built-in default a tenant that has chosen nothing publishes with. Only a global template. */
  built_in_default: boolean
  enabled: boolean
  read_only: boolean
  scope: 'global' | 'tenant'
  config: IntuneDeploymentTemplateConfig
  schema_version: number
  updated_at?: string | null
  used_by?: TemplateUsage
}

export const listIntuneTemplates = async (): Promise<{ templates: IntuneDeploymentTemplate[] }> => {
  const { data } = await tenantAPI.get('/intune-templates')
  return data
}

export const createIntuneTemplate = async (body: {
  name: string
  description?: string
  config: Partial<IntuneDeploymentTemplateConfig>
  is_default?: boolean
  enabled?: boolean
  // Optional {group_id: 'device'|'user'|'dynamic'} map so the backend can fully enforce
  // Microsoft's rule that one assignment must not mix user and device audiences across
  // include/exclude. Without it that check degrades to a warning, because a group's
  // audience lives on the group, not the assignment.
  group_audiences?: Record<string, string>
}): Promise<IntuneDeploymentTemplate> => {
  const { data } = await tenantAPI.post('/intune-templates', body)
  return data
}

export const updateIntuneTemplate = async (
  id: string,
  body: Partial<{
    name: string
    description: string
    config: Partial<IntuneDeploymentTemplateConfig>
    is_default: boolean
    enabled: boolean
    group_audiences: Record<string, string>
  }>,
): Promise<IntuneDeploymentTemplate> => {
  const { data } = await tenantAPI.put(`/intune-templates/${id}`, body)
  return data
}

export const deleteIntuneTemplate = async (id: string): Promise<void> => {
  await tenantAPI.delete(`/intune-templates/${id}`)
}

/** Copy an Intune deployment template into an editable, tenant-owned one. */
export const duplicateIntuneTemplate = async (id: string): Promise<IntuneDeploymentTemplate> => {
  const { data } = await tenantAPI.post(`/intune-templates/${id}/duplicate`)
  return data
}

/** Authoritative name preview. The wizard can render its own for responsiveness, but
 *  generated names are read-only at packaging time, so what publish will actually create
 *  has to come from the backend or an admin approves one name and gets another. */
export const previewIntuneNames = async (body: {
  naming?: Partial<IntuneNamingPatterns>
  appName?: string
  publisher?: string
  arch?: string
  version?: string
  intents?: string[]
}): Promise<{
  errors: string[]
  warnings: string[]
  app: string
  updateApp: string
  groups: string[]
  updateGroups: string[]
}> => {
  const { data } = await tenantAPI.post('/intune-templates/preview-names', body)
  return data
}

export interface TenantBranding {
  id: string
  product_name: string
  logo_url?: string | null
  logo_format?: string | null
}

export const getBranding = async (): Promise<{ branding: TenantBranding | null }> => {
  const { data } = await tenantAPI.get('/branding')
  return data
}

export const putBranding = async (productName: string, logo?: File): Promise<{ branding: TenantBranding }> => {
  const formData = new FormData()
  formData.append('product_name', productName)
  if (logo) {
    formData.append('logo', logo)
  }
  const { data } = await tenantAPI.put('/branding', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const deleteBranding = async (): Promise<void> => {
  await tenantAPI.delete('/branding')
}

// Intune Publish Preview
export interface IntunePublishPreview {
  app_name: string
  version: string
  install_command: string
  uninstall_command: string
  detection_rules: Array<{
    type: string
    keyPath: string
    valueName: string
    operator: string
    comparisonValue: string
  }>
  install_context: string
  architecture: string
  device_restart_behavior: string
  package_file: string | null
  package_size_mb: number | null
}

export const getIntunePublishPreview = async (appId: string): Promise<IntunePublishPreview> => {
  const { data } = await tenantAPI.post('/deployments/preview-intune-publish', { app_id: appId })
  return data
}

// ============================================================================
// Job Queue APIs (Portal Queue Controller)
// ============================================================================

import type {
  JobSubmitRequest,
  JobSubmitResponse,
  JobListParams,
  JobListResponse,
  PackagingJob,
  JobStatusResponse,
  BulkStatusResponse,
  JobArtifactsResponse,
  QueueMetrics,
} from '../types/job'
import type { UploadLimits } from './installerUpload'

/**
 * Submit a new packaging job
 * POST /jobs
 */
export const submitJob = async (request: JobSubmitRequest): Promise<JobSubmitResponse> => {
  const formData = new FormData()
  formData.append('source_type', request.source_type)
  formData.append('deployment_methods', JSON.stringify(request.deployment_methods))

  if (request.source_reference) {
    formData.append('source_reference', request.source_reference)
  }

  if (request.file) {
    formData.append('file', request.file)
  }

  if (request.upload_id) {
    formData.append('upload_id', request.upload_id)
  }

  if (request.installer_entry) {
    formData.append('installer_entry', request.installer_entry)
  }

  if (request.app_name?.trim()) {
    formData.append('app_name', request.app_name.trim())
  }

  if (request.target_app_id) {
    formData.append('target_app_id', request.target_app_id)
  }

  if (request.manual_params) {
    formData.append('manual_mode', 'true')
    formData.append('manual_params', JSON.stringify(request.manual_params))
  }

  // Pass agentic flag (default to true if not specified)
  formData.append('enable_agentic', String(request.enable_agentic !== false))

  // Pass learn_from source if specified (winget or choco)
  if (request.learn_from) {
    formData.append('learn_from', request.learn_from)
  }

  // Pass install_timeout if specified (for large installers like Adobe Reader)
  if (request.install_timeout) {
    formData.append('install_timeout', String(request.install_timeout))
  }

  // Pass version if specified (for winget/chocolatey version pinning)
  if (request.version) {
    formData.append('version', request.version)
  }

  // Pass skip_testing flag if enabled
  if (request.skip_testing) {
    formData.append('skip_testing', 'true')
  }

  // The discovered app this packaging job was started for. Discovery lists jobs by it, so a job
  // submitted without it simply never appears there.
  if (request.discovery_source) {
    formData.append('discovery_source', JSON.stringify(request.discovery_source))
  }

  const { data } = await tenantAPI.post('/jobs', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  // The header's task list reads the new job now rather than on its next poll.
  useTaskListStore.getState().notePackagingStarted()
  return data
}

/**
 * The resumable upload service's limits: the largest installer it accepts and its chunk size.
 * OPTIONS /uploads/
 */
export const uploadLimits = async (): Promise<UploadLimits> => {
  const res = await tenantAPI.options('/uploads/')
  const maxBytes = Number(res.headers['tus-max-size'])
  const chunkBytes = Number(res.headers['tus-chunk-size'])
  if (!Number.isFinite(maxBytes) || !Number.isFinite(chunkBytes) || chunkBytes <= 0) {
    throw new Error('The upload service did not report its limits')
  }
  const fileTypes = String(res.headers['upload-file-types'] || '.exe,.msi,.msix').split(',').filter(Boolean)
  return { maxBytes, chunkBytes, fileTypes }
}

/**
 * List jobs for current tenant
 * GET /jobs
 */
export const listJobs = async (params?: JobListParams): Promise<JobListResponse> => {
  const queryParams: Record<string, string> = {}

  if (params?.status && params.status.length > 0) {
    queryParams.status = params.status.join(',')
  }
  if (params?.limit !== undefined) {
    queryParams.limit = String(params.limit)
  }
  if (params?.offset !== undefined) {
    queryParams.offset = String(params.offset)
  }

  const { data } = await tenantAPI.get('/jobs', { params: queryParams })
  return data
}

/**
 * Get job details by ID
 * GET /jobs/{job_id}
 */
export const getJob = async (jobId: string): Promise<PackagingJob> => {
  const { data } = await tenantAPI.get(`/jobs/${jobId}`)
  return data
}

/**
 * Get lightweight job status (for polling)
 * GET /jobs/{job_id}/status
 */
export const getJobStatus = async (jobId: string): Promise<JobStatusResponse> => {
  const { data } = await tenantAPI.get(`/jobs/${jobId}/status`)
  return data
}

/**
 * Get bulk job status for multiple jobs
 * GET /jobs/status?ids=id1,id2,id3
 */
export const getBulkJobStatus = async (jobIds: string[]): Promise<BulkStatusResponse> => {
  const { data } = await tenantAPI.get('/jobs/status', {
    params: { ids: jobIds.join(',') }
  })
  return data
}

/**
 * Cancel a pending or queued job
 * POST /jobs/{job_id}/cancel
 */
export const cancelJob = async (jobId: string): Promise<PackagingJob> => {
  const { data } = await tenantAPI.post(`/jobs/${jobId}/cancel`)
  return data
}

/**
 * Retry a failed job
 * POST /jobs/{job_id}/retry
 */
export const retryJob = async (jobId: string): Promise<JobSubmitResponse> => {
  const { data } = await tenantAPI.post(`/jobs/${jobId}/retry`)
  return data
}

/** Publish anyway: publish a package whose test was blocked, after the user confirmed it. */
export const publishUntestedPackage = async (appId: string, jobId: string) => {
  const { data } = await tenantAPI.post(`/apps/${appId}/decision/publish`, { job_id: jobId, acknowledged: true })
  return data
}

/** Withdraw a package published anyway that has not been deployed. */
export const withdrawUntestedPackage = async (appId: string, jobId: string) => {
  const { data } = await tenantAPI.post(`/apps/${appId}/decision/withdraw`, { job_id: jobId })
  return data
}

/**
 * Stop a running job (forcefully terminate)
 * POST /jobs/{job_id}/stop
 */
export const stopJob = async (jobId: string): Promise<{ job_id: string; status: string; message: string }> => {
  const { data } = await tenantAPI.post(`/jobs/${jobId}/stop`)
  return data
}

/**
 * Delete a job and its artifacts
 * DELETE /jobs/{job_id}
 */
export const deleteJob = async (jobId: string): Promise<{ job_id: string; message: string }> => {
  const { data } = await tenantAPI.delete(`/jobs/${jobId}`)
  return data
}

/**
 * Get job artifacts list
 * GET /jobs/{job_id}/artifacts
 */
export const getJobArtifacts = async (jobId: string): Promise<JobArtifactsResponse> => {
  const { data } = await tenantAPI.get(`/jobs/${jobId}/artifacts`)
  return data
}

/**
 * Download job artifact
 * GET /jobs/{job_id}/artifacts/{artifact_id}
 * Returns blob for download
 */
export const downloadJobArtifact = async (jobId: string, artifactId: string): Promise<Blob> => {
  const { data } = await tenantAPI.get(`/jobs/${jobId}/artifacts/${artifactId}`, {
    responseType: 'blob'
  })
  return data
}

/**
 * Get artifact content as text (for logs and reports)
 * GET /jobs/{job_id}/artifacts/{artifact_id}/content
 */
export interface ArtifactContent {
  artifact_id: string
  artifact_type: string
  file_name: string
  mime_type: string
  content: string
}

export const getArtifactContent = async (jobId: string, artifactId: string): Promise<ArtifactContent> => {
  const { data } = await tenantAPI.get(`/jobs/${jobId}/artifacts/${artifactId}/content`)
  return data
}

/**
 * Get queue metrics (admin only)
 * GET /jobs/metrics
 */
export const getQueueMetrics = async (): Promise<QueueMetrics> => {
  const { data } = await tenantAPI.get('/jobs/metrics')
  return data
}

// ============================================================================
// WinGet Package Search APIs
// ============================================================================

export interface WinGetPackage {
  id: string
  name: string
  publisher: string
  version: string
  description: string
  homepage: string
  icon_url: string
  tags: string[]
}

export interface WinGetSearchResponse {
  packages: WinGetPackage[]
  total: number
  query: string
}

/**
 * Search WinGet packages
 * GET /winget/search
 */
export const searchWinGetPackages = async (query: string, limit: number = 20): Promise<WinGetSearchResponse> => {
  const { data } = await tenantAPI.get('/winget/search', {
    params: { query, limit }
  })
  return data
}

/**
 * Get WinGet package details by ID
 * GET /winget/package/{package_id}
 */
export const getWinGetPackage = async (packageId: string): Promise<WinGetPackage & { versions: string[] }> => {
  const { data } = await tenantAPI.get(`/winget/package/${packageId}`)
  return data
}

// Batch search types
export interface BatchSearchMatch {
  id: string
  name: string
  publisher: string
  version: string
  score: number
  source?: 'winget' | 'chocolatey'
}

export interface BatchSearchResult {
  app_name: string
  matches: BatchSearchMatch[]
  best_match: BatchSearchMatch | null
  confidence: 'high' | 'medium' | 'low' | 'none'
}

export interface BatchSearchResponse {
  results: BatchSearchResult[]
}

/**
 * Batch search WinGet for multiple app names
 * POST /winget/batch-search
 */
export const batchSearchWinGet = async (appNames: string[]): Promise<BatchSearchResponse> => {
  const { data } = await tenantAPI.post('/winget/batch-search', { app_names: appNames })
  return data
}

// ============================================================================
// Chocolatey Package Search APIs
// ============================================================================

/**
 * Search Chocolatey packages
 * GET /chocolatey/search
 */
export const searchChocolateyPackages = async (query: string, limit: number = 20): Promise<WinGetSearchResponse> => {
  const { data } = await tenantAPI.get('/chocolatey/search', {
    params: { query, limit }
  })
  return data
}

/**
 * Batch search Chocolatey for multiple app names
 * POST /chocolatey/batch-search
 */
export const batchSearchChocolatey = async (appNames: string[]): Promise<BatchSearchResponse> => {
  const { data } = await tenantAPI.post('/chocolatey/batch-search', { app_names: appNames })
  return data
}

// ============================================================================
// Discovery Match API (Add to Catalog)
// ============================================================================

// Source a discovered app can be brought into the catalog from, in priority order.
export type MatchSource = 'public_catalog' | 'private_catalog' | 'winget' | 'chocolatey'

// What selecting a candidate does: subscribe to a public-catalog app, recognise an
// already-owned private app, or submit a packaging job from WinGet/Chocolatey.
export type MatchAction = 'subscribe' | 'already_in_catalog' | 'package'

export interface MatchCandidate {
  source: MatchSource
  source_priority: number
  match_type: 'exact' | 'close'
  score: number
  arch_compatible: boolean
  version_match: boolean
  name: string
  version: string | null
  arch: string | null
  publisher: string | null
  /** `subscription_id` rides along on a library entry the tenant holds as a shortcut. */
  ref: { public_app_id?: string; tenant_app_id?: string; package_id?: string; subscription_id?: string }
  action: MatchAction
}

export interface DiscoveryMatchAppInput {
  id: string
  name: string
  version?: string | null
  arch?: string | null
  publisher?: string | null
  management_state?: string | null
  detected_app_id?: string | null
}

export interface DiscoveryMatchResult {
  app: DiscoveryMatchAppInput
  auto_selected: MatchCandidate | null
  needs_prompt: boolean
  confidence: 'high' | 'medium' | 'low' | 'none'
  candidates: MatchCandidate[]
}

/**
 * Match discovered apps against public catalog / private catalog / WinGet / Chocolatey.
 * POST /discovery/match
 */
export const discoveryMatch = async (
  apps: DiscoveryMatchAppInput[]
): Promise<{ results: DiscoveryMatchResult[] }> => {
  const { data } = await tenantAPI.post('/discovery/match', { apps })
  return data
}

// ----------------------------------------------------------------------------
// Convert unmanaged endpoint app -> managed (durable, reconciler-driven)
// ----------------------------------------------------------------------------

export type ConversionStateValue =
  | 'pending' | 'matched' | 'packaging' | 'publish_ready' | 'remediation_deployed'
  | 'uninstall_confirmed' | 'app_deploying' | 'rollout_active' | 'assigned'
  | 'verifying' | 'completed' | 'failed' | 'paused' | 'cancelled'

export interface ConversionJobSummary {
  id: string
  batch_id: string | null
  source_display_name: string
  source_version: string | null
  matched_app_name: string | null
  match_source: string | null
  deploy_mode: 'direct'
  state: ConversionStateValue
  is_terminal: boolean
  attempt_count: number
  error_message: string | null
  pause_reason: string | null
  device_total: number
  device_uninstalled: number
  device_installed: number
  device_failed: number
  created_at: string | null
  updated_at: string | null
  completed_at: string | null
  next_run_at: string | null
}

export interface ConversionDeviceDetail {
  device_name: string | null
  intune_device_id: string
  status: string
  uninstall_confirmed_at: string | null
  installed_at: string | null
  error_message: string | null
}

export interface ConversionPackagingInfo {
  job_id: string
  status: string | null
  progress_pct: number | null
  error_message: string | null
  result_app_id: string | null
  artifacts_path: string | null
  report: string | null  // AEAM packaging + sim-test report (markdown)
}

export interface ConversionJobDetail extends ConversionJobSummary {
  devices: ConversionDeviceDetail[]
  remediation_script_policy_id: string | null
  intune_app_id: string | null
  packaging?: ConversionPackagingInfo | null
}

export interface ConvertItem {
  source_display_name: string
  detected_app_id?: string | null
  source_version?: string | null
  match_mode?: 'exact' | 'wildcard'
  match_source?: string | null
  matched_ref?: string | null
  matched_app_name?: string | null
  silent_uninstall_args?: string | null
  intune_app_id?: string | null
}

export interface ConvertRequest {
  items: ConvertItem[]
  // Push options mirroring direct deployment, applied to the managed-app assignment.
  deploy_intent?: 'required' | 'available'
  assignment_settings?: AssignmentSettings | null
}

/** Create durable convert-to-managed jobs. POST /discovery/convert-to-managed */
export const convertToManaged = async (
  payload: ConvertRequest
): Promise<{ created: ConversionJobSummary[] }> => {
  const { data } = await tenantAPI.post('/discovery/convert-to-managed', payload)
  return data
}

/** List conversion jobs. GET /discovery/conversions */
export const listConversions = async (
  state?: string
): Promise<{ conversions: ConversionJobSummary[] }> => {
  const { data } = await tenantAPI.get('/discovery/conversions', {
    params: state ? { state } : undefined,
  })
  return data
}

/**
 * One packaging job started from Discovery: the discovered app it was started for, and the
 * package it produced. Distinct from `ConversionJobSummary`, which is the uninstall-then-redeploy
 * machine — this record only ever packages.
 */
export interface DiscoveryPackagingJob {
  job_id: string
  /** What Discovery did: built a package, or linked the library to a certified public one. */
  kind?: 'package' | 'shortcut'
  source_display_name: string | null
  source_publisher: string | null
  source_version: string | null
  detected_app_id: string | null
  /** Null until the job finishes and the package exists. */
  target_app_name: string | null
  /** What is being packaged (WinGet/Chocolatey package id, or the installer) — always present. */
  target_ref: string | null
  target_app_id: string | null
  status: 'queued' | 'packaging' | 'packaged' | 'failed' | 'cancelled' | 'linked'
  error_message: string | null
  created_at: string | null
  completed_at: string | null
}

/** Packaging jobs started from Discovery. GET /discovery/packaging-jobs */
export const listDiscoveryPackagingJobs = async (): Promise<{
  jobs: DiscoveryPackagingJob[]
  /** More records exist than the response carries — say so rather than showing a short list. */
  truncated?: boolean
  limit?: number
}> => {
  const { data } = await tenantAPI.get('/discovery/packaging-jobs')
  return data
}

/** Conversion detail incl. per-device sub-state. GET /discovery/conversions/{id} */
export const getConversion = async (jobId: string): Promise<ConversionJobDetail> => {
  const { data } = await tenantAPI.get(`/discovery/conversions/${jobId}`)
  return data
}

export const pauseConversion = (jobId: string) =>
  tenantAPI.post(`/discovery/conversions/${jobId}/pause`).then((r) => r.data as ConversionJobSummary)
export const resumeConversion = (jobId: string) =>
  tenantAPI.post(`/discovery/conversions/${jobId}/resume`).then((r) => r.data as ConversionJobSummary)
export const cancelConversion = (jobId: string) =>
  tenantAPI.post(`/discovery/conversions/${jobId}/cancel`).then((r) => r.data as ConversionJobSummary)
export const retryConversion = (jobId: string) =>
  tenantAPI.post(`/discovery/conversions/${jobId}/retry`).then((r) => r.data as ConversionJobSummary)
/** Advance the conversion one step immediately (don't wait for the ~10-min reconciler tick). */
export const runConversionStep = (jobId: string) =>
  tenantAPI.post(`/discovery/conversions/${jobId}/run-now`).then((r) => r.data as ConversionJobSummary)

// ============================================================================
// Deployments APIs
// ============================================================================

// Management state of a discovered app:
//   'sap'      - deployed/managed from our catalog
//   'intune'   - a Win32 app Intune manages, but not from our catalog
//   'endpoint' - detected installed on a device, managed by neither
export type ManagementState = 'sap' | 'intune' | 'endpoint'

export interface Deployment {
  id: string
  intune_app_id: string | null
  detected_app_id?: string | null
  app_name: string
  app_id: string | null
  target_type: 'intune' | 'sccm' | 'appstore_client' | 'endpoint'
  target_details: string
  status: 'active' | 'pending' | 'failed' | 'unknown' | 'removed' | 'detected'
  /** Whether the Intune app object currently carries any assignment. A published app with none
   *  reaches nobody, which is a different thing from its publishing state. `null` when it could
   *  not be read — a row Graph did not return, or a tenant that refused the assignments expand. */
  assigned?: boolean | null
  assignment_count?: number | null
  /** Whether Intune has the app object: true when Graph returned it, false when Graph was read
   *  and does not have it (deleted in the Intune admin center; the record is kept, Deploy
   *  publishes a new app and Remove drops the record), null when Intune was not read. */
  in_intune?: boolean | null
  version: string | null
  last_updated: string | null
  deployed_by: string
  publisher: string | null
  in_catalog: boolean
  management_state?: ManagementState
  /** Which half of the publish this row is. A template-driven publish creates two Intune apps:
   *  the install app, and the update-only twin restricted to devices that already have it.
   *  They are separate app ids with separate assignments. */
  role?: 'install' | 'update_only'
  /** Set on an update-only row: the install app it was published alongside. */
  parent_intune_app_id?: string | null
  /** Set on an install row that has an update-only twin. */
  update_only_app_id?: string | null
  /** A removal deleted this deployment's newer apps but Intune kept this one. It is not updated
   *  automatically; removing it again finishes the removal. */
  removal_incomplete?: boolean
  /** The app of a publish that failed after Intune created it: no assignments, content possibly
   *  missing. Never the deployment's install app; it stays in Intune until it is removed. */
  failed_publish?: boolean
  device_count?: number | null
  synced: boolean
  icon_url: string | null
  install_context?: 'system' | 'user' | null
  architecture?: 'x86' | 'x64' | 'arm64' | null
  /** Whether a newer version publishes on its own — the spec's `automatedUpdates.followNewVersions`.
   *  Absent for an app with no subscription, which behaves as manual. */
  update_mode?: 'FOLLOW' | 'PIN' | 'DISABLED' | null
  pinned_version?: string | null
  /** A newer version exists in the catalog and has not been pushed to Intune yet. */
  sync_pending?: boolean | null
  /** Where the automatic update of this deployment stands, as on DeploymentDetails, and why when
   *  it cannot publish on its own (cannot_rebuild, objects_differ). Present only for a package with
   *  a pending version. */
  update_state?: string | null
  update_to_version?: string | null
  update_ready_at?: string | null
  update_reason?: string | null
  /** The automated-update policy in force, resolved by the backend from the deployment template
   *  the app was published with and the per-subscription overrides. `update_mode` alone says
   *  whether a new version publishes on its own; these say WHEN, and 0 means immediately. */
  soak_days?: number | null
  follow_new_versions?: boolean | null
  /** The rest of this deployment's own update policy. */
  keep_versions?: number | null
  uninstall_previous?: boolean | null
  /** The policy values changed for this deployment only (followNewVersions, soakDays,
   *  keepVersions, uninstallPrevious); the others come from its deployment template. */
  update_policy_edited?: string[] | null
  /** Whether a new version reaches this deployment without someone deploying it. */
  updated_automatically?: boolean | null
  /** Which deployment of the package this app object belongs to; null is the first one. */
  deployment_uid?: string | null
  /** The deployment's name; null for the first deployment, which has none. */
  deployment_name?: string | null
  /** The same on every app object of one deployment: the list shows the deployment as one row.
   *  Null for an app no record names. */
  deployment_key?: string | null
  /** An edit made on the deployment page reached the deployment's other app but not this one. */
  out_of_sync?: boolean
  /** The catalog package's name (`app_name` is the Intune app's). */
  package_name?: string | null
  /** The catalog entry's status: approved, or deprecated / archived for an entry that left the
   *  catalog. Null for a tenant app. */
  catalog_status?: string | null
  /** For an entry that left the catalog, the package's current entry; null when there is none. */
  catalog_replaced_by?: { id: string; name: string; version: string } | null
  /** The templates the deployment uses; "Adjusted" when adjusted for it only. For a template that
   *  was deleted since, the name recorded at publish (null on older records) and `*_deleted`. */
  ux_template_name?: string | null
  ux_template_deleted?: boolean
  delivery_template_name?: string | null
  delivery_template_deleted?: boolean
}

/** One app of a deployment, as the deployment page names it. */
export interface DeploymentApp {
  role: 'install' | 'update_only'
  intune_app_id: string
  name: string | null
  in_intune: boolean | null
  /** Graph requirementRules of this app object; the update-only app's include its "app is
   *  installed" script rule. Null when the app was not read. */
  requirement_rules?: any[] | null
}

/** A shared setting on which the install app and the update-only app differ. `install` and
 *  `update` are null for settings too long to show side by side (rules, commands, notes). */
export interface DeploymentAppDifference {
  field: string
  label: string
  install: string | number | boolean | null
  update: string | number | boolean | null
}

/** An edit one app of the deployment missed; Retry sends it again. */
export interface MissedEdit {
  intune_app_id: string
  role: 'install' | 'update_only'
  section: string
  error: string | null
  at: string | null
}

export interface DeploymentsListResponse {
  deployments: Deployment[]
  total: number
  intune_configured: boolean
  intune_error: string | null
  detected_apps_supported?: boolean
  detected_apps_error?: string | null
}

/** The `automatedUpdates` block of a deployment template, as the API reports it. */
export interface IntuneAutomatedUpdatesSnapshot {
  followNewVersions?: boolean
  soakDays?: number
  keepVersions?: number
  supersede?: boolean
  uninstallPrevious?: boolean
}

export interface DeploymentDetails extends Deployment {
  /** The deployed version was published anyway, without a passing test (Needs your decision). */
  not_fully_tested?: boolean
  description?: string
  created_at?: string
  install_command?: string
  uninstall_command?: string
  /** Read off the package's metadata.json by the backend (api/deployments.py:112) and rendered in
   *  the details drawer. Both were missing from this interface, so the two reads of them were the
   *  page's only type errors — the fields are real, the type was not. */
  uninstall_commandline?: string
  uninstall_source?: string
  /** The -DeploymentConfig sidecar from the command line, decoded by the backend.
   *  This is what the deployment ACTUALLY shipped with — the commands are read back from Graph, not
   *  re-resolved from its template, which would differ the moment the template was edited. */
  deployment_config?: Record<string, any> | null
  /** The same settings as `deployment_config`, but in the nested authoring shape the UX wizard reads
   *  and its summary line renders (identity.*, closeApps.*, defer.*). `deployment_config` is the flat
   *  runtime sidecar and does not fit either — seeding the editor from it opens on defaults. */
  deployment_config_source?: Record<string, any> | null
  config_transport?: 'b64' | 'url' | null
  config_note?: string | null
  /** Whether Intune's current state still matches what we published.
   *  `comparable: false` means there is no baseline (published before commands were recorded, or not
   *  published by AEAM) — which is NOT drift, and must not be shown as one. */
  drift?: {
    comparable: boolean
    drifted: boolean
    changes: { field: string; ours: any; theirs: any }[]
    note?: string | null
  } | null
  /** What this rollout WENT OUT WITH, read back off the Intune app object rather than re-resolved
   *  from its template — the template moves on, the deployment does not (spec §7a). */
  intune_metadata?: {
    developer?: string | null
    owner?: string | null
    notes?: string | null
    informationUrl?: string | null
    privacyUrl?: string | null
    featured?: boolean | null
    /** Company Portal categories bound to the app. A navigation property, so the backend reads
     *  them separately from the app object. */
    categories?: string[] | null
  } | null
  advanced?: {
    returnCodes?: { returnCode: number; type: string }[] | null
    installExperience?: { runAsAccount?: string; deviceRestartBehavior?: string } | null
    minimumOs?: string | null
    architectures?: string | null
    minimumFreeDiskSpaceMB?: number | null
    minimumMemoryMB?: number | null
    minimumProcessors?: number | null
    minimumCpuSpeedMHz?: number | null
    requirementRules?: any[] | null
  } | null
  allow_uninstall_from_company_portal?: boolean | null
  /** Every publish of this package, newest first, from the publish audit entries. A version whose
   *  Intune app object retention has since deleted still shipped, so it stays in the list. */
  versions?: PublishedVersion[]
  /** The user-experience template this rollout was published from, and whether its settings were
   *  adjusted for this rollout only. */
  ux_template?: { id: string | null; name: string | null; adjusted: boolean } | null
  /** The automated-update policy AS PUBLISHED — the audit answer to what this rollout went out
   *  with. Editing the template afterwards does not change it, so it is NOT what a new version
   *  would do today: that is `automated_updates_effective`. */
  automated_updates_published?: IntuneAutomatedUpdatesSnapshot | null
  /** The policy a new version would be published under TODAY: the template as it stands now,
   *  under this subscription's overrides. */
  automated_updates_effective?: IntuneAutomatedUpdatesSnapshot | null
  /** Where an automatic update of this deployment stands, while automatic updates act on every
   *  deployment: up_to_date, waiting, due, job_open, failed, not_following, held,
   *  cannot_rebuild, objects_differ or removal_pending. */
  update_state?: string | null
  update_to_version?: string | null
  update_ready_at?: string | null
  update_reason?: string | null
  /** The sections of this app object edited on the deployment page. Kept by a deploy that keeps
   *  the template governing them. */
  overrides?: Partial<
    Record<OverrideSection, { at?: string | null; by?: string | null; fields?: Record<string, any>; rows?: any[] }>
  > | null
  /** The app the deployment's page stands for: the install app, or the update-only app when
   *  the deployment has no install app. */
  primary_app_id?: string
  /** Every app of the deployment, install first. */
  apps?: DeploymentApp[]
  missed_edits?: MissedEdit[]
  /** Null when the deployment has one app or either app could not be read. */
  app_differences?: DeploymentAppDifference[] | null
  packaging_mode?: string
  subscription_id?: string
  checksum_mode?: string
  catalog_info?: {
    display_name: string
    description: string
    category: string
    icon_url: string
  }
}

/** One row of the deployment page's device table, from the DeviceInstallStatusByApp report.
 *  The report carries no device model, and both its user columns are empty for a device-context
 *  install — an unnamed user is the truth there, not a missing read. */
export interface DeviceInstallStatus {
  deviceId: string | null
  deviceName: string | null
  user: string | null
  /** The report's `Platform`, e.g. "Windows 10.0.26200.9168". */
  os: string | null
  /** mobileAppInstallStatus names: installed | failed | notInstalled | notApplicable |
   *  uninstallFailed | pendingInstall | unknown. */
  installState: string | null
  appVersion: string | null
  errorCode: string | null
  lastCheckIn: string | null
  /** Branding only, where the audience is resolved to devices: whether this machine is one the
   *  deployment currently reaches. A row with `false` reported on the application while OUT of the
   *  audience — it holds branding that no longer reaches it. Absent on application deployments,
   *  which list what the report carried and nothing else. */
  inAudience?: boolean
  /** Which targets put this device in the audience. Empty for a device outside it. */
  assignedVia?: AudienceTarget[]
}

export interface PublishedVersion {
  version: string | null
  publishedAt: string | null
  intuneAppId: string | null
  /** `null` when we could not ask Intune — distinct from "the object is gone". */
  inIntune: boolean | null
}

/**
 * List all deployments for the current tenant
 * GET /deployments
 */
export const listDeployments = async (): Promise<DeploymentsListResponse> => {
  const { data } = await tenantAPI.get('/deployments')
  return data
}

/**
 * Get deployment details
 * GET /deployments/{deployment_id}
 */
export const getDeploymentDetails = async (deploymentId: string): Promise<DeploymentDetails> => {
  const { data } = await tenantAPI.get(`/deployments/${deploymentId}`)
  return data
}

export interface DeploymentDeviceStatus {
  /** The four counts the page leads with. `null` when the report could not be read — which is not
   *  the same as four zeros, and must not render as "nobody has it". */
  install_stats: {
    installed: number
    pending: number
    failed: number
    notApplicable: number
  } | null
  /** Per-device install status, the list the counts above summarise. `null` when the report could
   *  not be read — which is not "no devices", and must not render as an empty table. */
  device_statuses: DeviceInstallStatus[] | null
  /** "preparing" while Intune's report export is still running (ask again in a few seconds),
   *  "error" when it failed (`error` says so plainly; asking again starts a new export). */
  state?: 'ready' | 'preparing' | 'error'
  error?: string
}

/**
 * Per-device install status for a deployment.
 * GET /deployments/{deployment_id}/device-status
 *
 * Its own request because it runs Intune's DeviceInstallStatusByApp report export, which takes
 * far longer than the rest of the page — see the endpoint's own note.
 */
export const getDeploymentDeviceStatus = async (
  deploymentId: string,
): Promise<DeploymentDeviceStatus> => {
  const { data } = await tenantAPI.get(`/deployments/${deploymentId}/device-status`)
  return data
}

/**
 * Redeploy (PATCH) an existing Intune app's metadata in-place
 * POST /deployments/{deployment_id}/redeploy
 */
export const redeployToIntune = async (deploymentId: string): Promise<{
  success: boolean
  message: string
  deployment_id: string
  updated_fields: {
    setupFilePath: string
    installCommandLine: string
    uninstallCommandLine: string
  }
}> => {
  const { data } = await tenantAPI.post(`/deployments/${deploymentId}/redeploy`)
  return data
}

/**
 * Delete a deployment from its publisher (e.g., Intune)
 * DELETE /deployments/{deployment_id}
 */
export const deleteDeployment = async (deploymentId: string, keepUpdateOnly = false): Promise<{
  /** False with status 'partial': the app is gone but an older version of it could not be
   *  deleted and is still in Intune. `detail` says which and why. */
  success: boolean
  status: 'removed' | 'partial'
  message: string
  detail?: string
  deployment_id: string
  updated_app_id: string | null
  related?: RelatedIntuneApp[]
}> => {
  // keep_update_only leaves an install app's update-only app as a deployment of its own.
  const { data } = await tenantAPI.delete(`/deployments/${deploymentId}`, {
    params: keepUpdateOnly ? { keep_update_only: true } : undefined,
  })
  return data
}

/** Another Intune app object of the same deployment, deleted when the deployment is removed. */
export interface RelatedIntuneApp {
  intune_app_id: string
  role: 'update_only' | 'older_update_only' | 'older_version'
  version: string | null
  /** From Intune; null when Intune could not be read. */
  display_name?: string | null
  assignment_count?: number | null
  /** Set on a removal result. 'skipped' = not attempted because an earlier delete failed. */
  status?: 'removed' | 'not_found' | 'error' | 'skipped'
  detail?: string
}

/** An Intune app outside a removal that holds a relationship over one of the apps it deletes. */
export interface ForeignIntuneRelationship {
  intune_app_id: string
  display_name: string | null
  relationship: 'supersedence' | 'dependency'
  /** The app of the removal that the relationship is held over. */
  over_intune_app_id: string
}

export interface RemovalPreviewItem {
  deployment_id: string
  status: 'ok' | 'invalid_id'
  /** null for an Intune app no deployment record names: only that one app is deleted. */
  role?: 'install' | 'update_only' | null
  app_name?: string | null
  assignment_count?: number | null
  related: RelatedIntuneApp[]
  /** Apps that supersede one of the deleted apps; the removal takes that supersedence off them.
   *  Null when Intune could not be read. */
  relationships?: ForeignIntuneRelationship[] | null
  /** Apps that depend on one of the deleted apps. Intune refuses the delete while they do, so
   *  the removal refuses too until the dependency is removed in Intune. Null when Intune could
   *  not be read. */
  blocked_by?: ForeignIntuneRelationship[] | null
  /** The Entra groups the deployment's publishes created, by name. The removal leaves them in
   *  place. Null when no publish record says which; empty when the record lists none. */
  groups?: string[] | null
}

export interface RemovalPreviewBody {
  /** False when Intune could not be read: names and assignment counts are then null. */
  intune_readable: boolean
  deployments: RemovalPreviewItem[]
}

/**
 * What removing these deployments deletes from Intune. Reads only.
 * POST /deployments/removal-preview
 *
 * Asks for both settings of keep_update_only at once: the body is the preview for
 * keep_update_only=false, and `keep_update_only` holds the preview for keep_update_only=true.
 * A server that does not return `keep_update_only` answers for false only.
 */
export const previewDeploymentRemoval = async (deploymentIds: string[]): Promise<
  RemovalPreviewBody & { keep_update_only?: RemovalPreviewBody | null }
> => {
  const { data } = await tenantAPI.post('/deployments/removal-preview', {
    deployment_ids: deploymentIds,
    keep_update_only: false,
    both_choices: true,
  })
  return data
}

export interface BulkRemoveResult {
  deployment_id: string
  status: 'removed' | 'partial' | 'not_found' | 'invalid_id' | 'error'
  detail?: string
  app_name?: string | null
  related?: RelatedIntuneApp[]
}

/**
 * Remove several deployments from Intune in one call.
 * POST /deployments/bulk-remove
 *
 * Partial failure is normal — an app already gone from Intune, a stale id, revoked permissions.
 * `success` is true ONLY when every id was removed; read `results` for the per-id outcome
 * rather than treating a 200 as "all done".
 */
export const bulkRemoveDeployments = async (deploymentIds: string[], keepUpdateOnly = false): Promise<{
  success: boolean
  requested: number
  removed: number
  failed: number
  results: BulkRemoveResult[]
}> => {
  const { data } = await tenantAPI.post('/deployments/bulk-remove', {
    deployment_ids: deploymentIds,
    keep_update_only: keepUpdateOnly,
  })
  return data
}

// importFromIntune removed — replaced by batchSearchWinGet + submitJob flow

// ============================================================================
// Events API (Monitoring Dashboard)
// ============================================================================

export interface EventData {
  id: string
  tenant_id: string
  user_id: string | null
  user_email: string | null
  user_name: string | null
  event_type: string
  severity: string
  title: string
  description: string | null
  resource_type: string | null
  resource_id: string | null
  resource_name: string | null
  event_data: Record<string, unknown> | null
  ip_address: string | null
  created_at: string
}

export interface EventsListResponse {
  events: EventData[]
  total: number
  limit: number
  offset: number
}

/**
 * List events for the current tenant
 * GET /events
 */
export const listEvents = async (params?: {
  event_type?: string
  severity?: string
  search?: string
  date_from?: string
  date_to?: string
  limit?: number
  offset?: number
}): Promise<EventsListResponse> => {
  const { data } = await tenantAPI.get('/events', { params })
  return data
}

export interface EventStats {
  total: number
  success: number
  warning: number
  error: number
  info: number
}

/**
 * Count the tenant's events per severity — the monitoring cards.
 * Takes the same filters as the list except severity, which is what it counts.
 * GET /events/stats
 */
export const getEventStats = async (params?: {
  event_type?: string
  search?: string
  date_from?: string
  date_to?: string
}): Promise<EventStats> => {
  const { data } = await tenantAPI.get('/events/stats', { params })
  return data
}

/**
 * Get available event types and severities
 * GET /events/types
 */
export const getEventTypes = async (): Promise<{
  event_types: string[]
  severities: string[]
}> => {
  const { data } = await tenantAPI.get('/events/types')
  return data
}

// ============================================================================
// Azure AD Groups API
// ============================================================================

export interface AzureADGroup {
  id: string
  displayName: string
  description: string | null
  membershipRule: string | null
  /** Entra's Membership type: how the group gets its members. */
  membershipType?: 'assigned' | 'dynamic'
  /** What the members are, derived from the membership rule. 'mixed' means it cannot be
   *  determined — an assigned group can hold both users and devices. */
  memberType?: 'device' | 'user' | 'mixed'
}

export interface AzureADGroupsResponse {
  groups: AzureADGroup[]
  total: number
}

/**
 * List Azure AD groups for assignment targeting
 * GET /integrations/intune/groups
 */
export interface IntuneAssignmentFilter {
  id: string
  name: string
  platform?: string | null
  description?: string | null
}

/**
 * Intune assignment filters. Added because AssignmentBlade's filter picker had no source — the
 * control, the payload mapper and the API all handled a filter, and nothing could list one.
 * GET /integrations/intune/assignment-filters
 */
export interface CommandPreview {
  install_command: string
  uninstall_command: string
  deployment_config: Record<string, any> | null
  config_transport: 'b64' | 'url' | null
  interactive: boolean
  uninstall_carries_config: boolean
  warnings: string[]
}

/**
 * The exact PSADT command line a publish WOULD run, for a config that need not be saved yet.
 * Read-only: no Graph call, no app, no tenant mutation — so it is safe to call from a wizard on
 * every click.
 *
 * Shares the publish path's own builders server-side. A preview assembled separately would agree
 * with itself and disagree with production, which is worse than having none.
 * POST /publishing/preview-commands
 */
export interface ConfigBudget {
  /** Bytes this draft spends of the Intune install command. */
  used: number | null
  budget: number
  remaining: number | null
  /** False means saving will be refused. The same measurement the save check makes, so the two
   *  cannot disagree — an author told it fits is not then refused. */
  fits: boolean
}

/** What a DRAFT configuration costs, measured as the save path measures it.
 *
 *  A probe: it stores nothing and validates nothing, so it can be asked on every keystroke. */
export const getConfigBudget = async (configJson: Record<string, any>): Promise<ConfigBudget> => {
  const { data } = await tenantAPI.post('/templates/budget', { config_json: configJson })
  return data
}

export const previewCommands = async (
  inlineConfig: Record<string, any>
): Promise<CommandPreview> => {
  const { data } = await tenantAPI.post('/publishing/preview-commands', {
    inline_config: inlineConfig,
  })
  return data
}

export const listAssignmentFilters = async (): Promise<{ filters: IntuneAssignmentFilter[] }> => {
  const { data } = await tenantAPI.get('/integrations/intune/assignment-filters')
  return data
}

export interface IntuneAppCategory {
  id: string
  name: string
}

/**
 * The tenant's own Company Portal categories — Intune's built-in set plus any it has created.
 * Intune has no fixed category vocabulary, so a picker cannot hardcode one.
 *
 * `available` distinguishes "this tenant has no categories" from "we could not ask Intune"; the
 * picker must not present the second as the first.
 * GET /integrations/intune/categories
 */
export const listIntuneAppCategories = async (): Promise<{
  categories: IntuneAppCategory[]
  available: boolean
}> => {
  const { data } = await tenantAPI.get('/integrations/intune/categories')
  return data
}

export const listAzureADGroups = async (search?: string): Promise<AzureADGroupsResponse> => {
  const params: Record<string, string> = {}
  if (search) {
    params.search = search
  }
  const { data } = await tenantAPI.get('/integrations/intune/groups', { params })
  return data
}

// ============================================================================
// Assignment Management APIs
// ============================================================================

export interface AssignmentTarget {
  type: 'group' | 'allUsers' | 'allDevices'
  id?: string
  name?: string
  include: boolean
  /** Intune assignment-filter id scoping this target, and whether it includes or excludes.
   *  Both are on the backend model (api/deployments.py:1282) and were simply absent here, so the
   *  filter an admin picked in AssignmentBlade had nowhere to go on the way out. */
  filter_id?: string
  filter_type?: 'include' | 'exclude'
}

export interface AssignmentSettings {
  /** OUR vocabulary, not Graph's. The API takes `onRestart` and translates it to Graph's
   *  `showReboot` on the way out (intune_publisher.GRAPH_NOTIFICATION), and translates back on
   *  the way in — this said `showReboot`, which is a value the API never sends or accepts. */
  notifications?: 'showAll' | 'onRestart' | 'hideAll'
  delivery_optimization?: 'notConfigured' | 'foreground'
  /** Company Portal auto-update of a superseded app. No control edits it; it round-trips so that
   *  saving the assignment set does not switch it off. */
  auto_update_superseded_apps?: boolean
  restart_settings?: {
    grace_period_minutes: number
    countdown_minutes: number
    snooze_minutes: number
  }
  install_time_settings?: {
    use_local_time: boolean
    start_datetime?: string
    deadline_datetime?: string
  }
}

export interface Assignment {
  id: string
  intent: 'required' | 'available' | 'availableWithoutEnrollment' | 'uninstall'
  target: AssignmentTarget
  settings: AssignmentSettings
}

export interface AssignmentCreate {
  intent: 'required' | 'available' | 'availableWithoutEnrollment' | 'uninstall'
  target: AssignmentTarget
  settings?: AssignmentSettings
}

export interface AssignmentsListResponse {
  assignments: Assignment[]
  total: number
}

export interface AssignmentsCreateResponse {
  success: boolean
  created_assignments: Array<{
    id: string
    intent: string
    target_type: string
    target_id?: string
    include: boolean
  }>
  errors?: Array<{
    intent: string
    target_type: string
    error: string
  }>
  message: string
}

/**
 * List assignments for a deployment
 * GET /deployments/{deployment_id}/assignments
 */
/**
 * Replace a deployed app's COMPLETE assignment set — spec §7a.2.
 *
 * Not `createAssignments`: that appends. The Assign screen edits the whole set, so saving through
 * the append endpoint would re-create every untouched row and could never remove one.
 * PUT /deployments/{id}/assignments
 */
export const replaceAssignments = async (
  deploymentId: string,
  assignments: AssignmentCreate[],
): Promise<{ success: boolean; count: number; message: string; warnings?: string[] }> => {
  const { data } = await tenantAPI.put(`/deployments/${deploymentId}/assignments`, { assignments })
  return data
}

export const listAssignments = async (deploymentId: string): Promise<AssignmentsListResponse> => {
  const { data } = await tenantAPI.get(`/deployments/${deploymentId}/assignments`)
  return data
}

/**
 * Create assignments for a deployment
 * POST /deployments/{deployment_id}/assignments
 */
export const createAssignments = async (
  deploymentId: string,
  assignments: AssignmentCreate[]
): Promise<AssignmentsCreateResponse> => {
  const { data } = await tenantAPI.post(`/deployments/${deploymentId}/assignments`, {
    assignments
  })
  return data
}

/**
 * Delete an assignment
 * DELETE /deployments/{deployment_id}/assignments/{assignment_id}
 */
export const deleteAssignment = async (
  deploymentId: string,
  assignmentId: string
): Promise<{ success: boolean; message: string; assignment_id: string }> => {
  const { data } = await tenantAPI.delete(`/deployments/${deploymentId}/assignments/${assignmentId}`)
  return data
}

// ============================================================================
// App Subscription APIs (Public Catalog Auto-Update)
// ============================================================================

export type UpdateMode = 'FOLLOW' | 'PIN' | 'DISABLED'
export type SubscriptionStatus = 'ACTIVE' | 'PAUSED' | 'REVOKED'

export interface AppSubscription {
  id: string
  tenant_id: string
  public_app_id: string
  tenant_app_id: string | null
  status: SubscriptionStatus
  update_mode: UpdateMode
  pinned_version: string | null
  current_synced_version: string | null
  latest_available_version: string | null
  sync_pending: boolean
  last_sync_at: string | null
  created_at: string
  updated_at: string
  soak_days?: number | null
  keep_versions?: number | null
  uninstall_previous?: boolean | null
}

export interface SubscriptionWithApp extends AppSubscription {
  public_app: {
    id: string
    name: string
    display_name: string
    publisher: string
    version: string
    icon_url: string | null
    auto_update_enabled: boolean
    update_status: string
  }
}

export interface SubscribeRequest {
  public_app_id: string
  update_mode?: UpdateMode
  pinned_version?: string
  /** Why the library entry is being created. Only Discovery sets it. */
  origin?: 'conversion'
  /** The discovered app the conversion was made for; Discovery lists the entry by it. */
  discovery_source?: {
    display_name: string
    publisher?: string | null
    version?: string | null
    detected_app_id?: string | null
  }
}

export interface SubscriptionUpdateRequest {
  update_mode?: UpdateMode
  pinned_version?: string
  status?: SubscriptionStatus
  soak_days?: number
  keep_versions?: number
  uninstall_previous?: boolean
}

/**
 * List all subscriptions for current tenant
 * GET /subscriptions
 */
export const listSubscriptions = async (): Promise<{ subscriptions: SubscriptionWithApp[]; total: number }> => {
  const { data } = await tenantAPI.get('/subscriptions')
  return data
}

/**
 * Subscribe to a public catalog app
 * POST /subscriptions
 */
export const subscribeToApp = async (request: SubscribeRequest): Promise<AppSubscription> => {
  const { data } = await tenantAPI.post('/subscriptions', request)
  return data
}

/**
 * Get subscription details
 * GET /subscriptions/{subscription_id}
 */
export const getSubscription = async (subscriptionId: string): Promise<SubscriptionWithApp> => {
  const { data } = await tenantAPI.get(`/subscriptions/${subscriptionId}`)
  return data
}

/**
 * Update subscription settings
 * PATCH /subscriptions/{subscription_id}
 */
export const updateSubscription = async (
  subscriptionId: string,
  update: SubscriptionUpdateRequest
): Promise<AppSubscription> => {
  const { data } = await tenantAPI.patch(`/subscriptions/${subscriptionId}`, update)
  return data
}

/**
 * Unsubscribe from a public app
 * DELETE /subscriptions/{subscription_id}
 */
export const unsubscribe = async (subscriptionId: string): Promise<{ message: string }> => {
  const { data } = await tenantAPI.delete(`/subscriptions/${subscriptionId}`)
  return data
}

/**
 * Set checksum validation mode for a subscription
 * PATCH /subscriptions/{subscription_id}/checksum-mode
 */
export const setChecksumMode = async (subscriptionId: string, checksumMode: string): Promise<{ checksum_mode: string; message: string }> => {
  const { data } = await tenantAPI.patch(`/subscriptions/${subscriptionId}/checksum-mode`, { checksum_mode: checksumMode })
  return data
}

/**
 * Update uninstall command for a deployed app
 * PATCH /deployments/{deployment_id}/uninstall-command
 */
export const updateUninstallCommand = async (
  deploymentId: string,
  uninstallCommandline: string
): Promise<{ message: string; intune_updated: boolean; metadata_updated: boolean }> => {
  const { data } = await tenantAPI.patch(`/deployments/${deploymentId}/uninstall-command`, {
    uninstall_commandline: uninstallCommandline
  })
  return data
}

/**
 * Update install command for a deployed app
 * PATCH /deployments/{deployment_id}/install-command
 */
export const updateInstallCommand = async (
  deploymentId: string,
  installCommandline: string
): Promise<{ message: string; intune_updated: boolean; metadata_updated: boolean }> => {
  const { data } = await tenantAPI.patch(`/deployments/${deploymentId}/install-command`, {
    install_commandline: installCommandline
  })
  return data
}

export interface AppInformationFields {
  owner?: string
  developer?: string
  notes?: string
  information_url?: string
  privacy_url?: string
  is_featured?: boolean
  allow_uninstall?: boolean
  categories?: string[]
}

/**
 * Edit App-information (owner/developer/notes/URLs/Featured/allow-uninstall) on the Intune app.
 * PATCH /deployments/{deployment_id}/app-information
 */
export const updateAppInformation = async (
  deploymentId: string,
  fields: AppInformationFields
): Promise<{
  message: string
  applied: { v1_fields: string[]; beta_fields: string[] }
  /** Settings Intune did not accept while the rest were saved, e.g. allowAvailableUninstall. */
  warnings?: string[]
  /** The apps of the deployment the edit did not reach. */
  out_of_sync?: string[]
}> => {
  const { data } = await tenantAPI.patch(`/deployments/${deploymentId}/app-information`, fields)
  return data
}

export interface DeploymentPolicyEdit {
  follow_new_versions?: boolean
  soak_days?: number
  keep_versions?: number
  uninstall_previous?: boolean
  /** Changed values to drop back to the deployment template's. */
  clear?: Array<'follow_new_versions' | 'soak_days' | 'keep_versions' | 'uninstall_previous'>
}

/**
 * Change the update policy of the deployment this Intune app belongs to; no other deployment of
 * the package changes.
 * PATCH /deployments/{deployment_id}/update-policy
 */
export const updateDeploymentPolicy = async (
  deploymentId: string,
  edit: DeploymentPolicyEdit,
): Promise<{
  deployment_id: string
  deployment_uid: string | null
  follow_new_versions: boolean
  soak_days: number
  keep_versions: number
  uninstall_previous: boolean
  update_policy_edited: string[]
}> => {
  const { data } = await tenantAPI.patch(`/deployments/${deploymentId}/update-policy`, edit)
  return data
}

export interface AdvancedFields {
  minimum_os?: string
  disk_space_mb?: number
  memory_mb?: number
  logical_processors?: number
  cpu_speed_mhz?: number
  architectures?: string[]
  rules?: any[]
  return_codes?: { returnCode: number; type: string }[]
  restart_behaviour?: string
}

/**
 * Edit Advanced requirements (architecture + minimum OS/disk/memory) on the Intune app.
 * PATCH /deployments/{deployment_id}/advanced
 */
export const updateAdvanced = async (
  deploymentId: string,
  fields: AdvancedFields
): Promise<{
  message: string
  /** Null when the beta PATCH was refused; `warnings` then says what was not saved. */
  applied: { fields: string[]; beta_fields: string[] } | null
  /** Settings Intune did not accept while the rest were saved, e.g. allowedArchitectures. */
  warnings?: string[]
  /** The apps of the deployment the edit did not reach. */
  out_of_sync?: string[]
}> => {
  const { data } = await tenantAPI.patch(`/deployments/${deploymentId}/advanced`, fields)
  return data
}

export interface ExperienceFields {
  interactive?: boolean
  prompt_condition?: 'always' | 'ifRunning'
  defer_times?: number
  countdown_seconds?: number
  /** The full UxTemplate config from the experience wizard (apply mode) — re-renders the sidecar. */
  config?: any
}

/**
 * Edit the user experience (interactive / prompt timing / deferrals / countdown) on a deployment.
 * Merged into the install command's -DeploymentConfig sidecar on the backend.
 * PATCH /deployments/{deployment_id}/experience
 */
export const updateExperience = async (
  deploymentId: string,
  fields: ExperienceFields
): Promise<{
  message: string
  /** What was not saved, e.g. the write to the deployment's other app failing. */
  warnings?: string[]
  /** The apps of the deployment the edit did not reach. */
  out_of_sync?: string[]
}> => {
  const { data } = await tenantAPI.patch(`/deployments/${deploymentId}/experience`, fields)
  return data
}

/**
 * Send again every edit an app of the deployment missed.
 * POST /deployments/{deployment_id}/retry-sync
 */
export const retryDeploymentSync = async (deploymentId: string): Promise<{
  applied: { intune_app_id: string; role: string; section: string }[]
  failed: { intune_app_id: string; role: string; section: string; error: string }[]
  in_sync: boolean
}> => {
  const { data } = await tenantAPI.post(`/deployments/${deploymentId}/retry-sync`)
  return data
}

/**
 * Copy the install app's shared settings to the update-only app of the deployment.
 * POST /deployments/{deployment_id}/make-identical
 */
export const makeDeploymentAppsIdentical = async (deploymentId: string): Promise<{
  copied: string[]
  warnings: string[]
  remaining: DeploymentAppDifference[]
}> => {
  const { data } = await tenantAPI.post(`/deployments/${deploymentId}/make-identical`)
  return data
}

/**
 * Publish again the update app of a deployment, deleted in Intune, with the deployment's own
 * settings. Starts a deployment job and returns it; refused (409) with a plain reason otherwise.
 * POST /deployments/{id}/recreate-update-app
 */
export const recreateUpdateApp = async (deploymentId: string): Promise<DeploymentJob> => {
  const { data } = await tenantAPI.post(`/deployments/${deploymentId}/recreate-update-app`)
  return data
}

/**
 * Check if tenant is subscribed to a specific app
 * GET /subscriptions/by-app/{public_app_id}
 */
export const checkSubscription = async (publicAppId: string): Promise<AppSubscription | null> => {
  const { data } = await tenantAPI.get(`/subscriptions/by-app/${publicAppId}`)
  return data
}

/**
 * Manually sync a subscription to latest version
 * POST /subscriptions/{subscription_id}/sync
 */
export const syncSubscription = async (subscriptionId: string): Promise<{
  success: boolean
  previous_version: string | null
  new_version: string
  tenant_app_updated?: boolean
  intune_superseded?: boolean
  warnings?: string[]
}> => {
  const { data } = await tenantAPI.post(`/subscriptions/${subscriptionId}/sync`)
  return data
}

/**
 * Sync all subscriptions to their latest versions
 * POST /subscriptions/sync-all
 */
export const syncAllSubscriptions = async (): Promise<{
  total: number
  synced: number
  already_current: number
  failed: number
  details: Array<{
    subscription_id: string
    success: boolean
    previous_version: string | null
    new_version: string | null
    message: string
    tenant_app_updated?: boolean
    intune_superseded?: boolean
    warnings?: string[]
  }>
}> => {
  const { data } = await tenantAPI.post('/subscriptions/sync-all')
  return data
}

/**
 * Get version history for a public catalog app with sync/deployment annotations
 * GET /apps/public-catalog/{app_id}/versions
 */
export interface PublicAppVersionEntry {
  id: string | null
  version: string
  status: string
  created_at: string | null
  published_at: string | null
  superseded_at: string | null
  test_result: string | null
  source_job_id: string | null
  /** The version's package folder is still on disk, so it can be downloaded. */
  has_package?: boolean
  synced?: boolean
  intune_deployed?: boolean
  intune_action?: string
  intune_app_id?: string
  intune_deployed_at?: string
  install_context?: 'system' | 'user' | null
  architecture?: 'x86' | 'x64' | 'arm64' | null
}

export interface PublicAppVersionHistory {
  app_id: string
  app_name: string
  current_version: string
  /** What the package was built from (winget | chocolatey | file_upload | url), or null. */
  source_type?: string | null
  /** The entry's place in the catalog. Not deployable when it was superseded (deprecated) or
   *  removed (archived); `replaced_by` is then the package's current entry, if it has one. */
  catalog_entry?: {
    status: string
    deployable: boolean
    replaced_by: { id: string; name: string; version: string } | null
  }
  versions: PublicAppVersionEntry[]
  subscription: {
    id: string
    update_mode: string
    current_synced_version: string | null
    latest_available_version: string | null
    last_sync_at: string | null
  } | null
  deployment_history: Array<{
    type: string
    intune_app_id: string
    version: string
    deployed_at: string | null
    action: string
    previous_intune_app_id?: string
    install_context?: 'system' | 'user' | null
    architecture?: 'x86' | 'x64' | 'arm64' | null
  }>
}

/** One page of the approved public catalog packages, with the ids bundles and deployments take.
 *  The route caps a page at 100. */
export const fetchPublicCatalogPage = async (params: {
  limit: number
  offset: number
}): Promise<FetchAppsResponse> => {
  const { data } = await tenantAPI.get('/apps/public-catalog', { params })
  return { apps: data.apps || [], total: data.total || data.apps?.length || 0 }
}

/** What each public catalog app was built from, keyed by app id; apps with no record are absent. */
export const getPublicAppSourceTypes = async (ids: string[]): Promise<Record<string, string>> => {
  const { data } = await tenantAPI.get('/apps/public-catalog/source-types', { params: { ids: ids.join(',') } })
  return data.source_types || {}
}

export const getPublicAppVersionHistory = async (appId: string): Promise<PublicAppVersionHistory> => {
  const { data } = await tenantAPI.get(`/apps/public-catalog/${appId}/versions`)
  return data
}

/**
 * Get public app with auto-update info
 * GET /apps/public-catalog/{app_id}
 */
export const getPublicAppWithAutoUpdate = async (appId: string): Promise<{
  id: string
  name: string
  display_name: string
  description: string
  publisher: string
  version: string
  icon_url: string | null
  auto_update_enabled: boolean
  update_status: string
  latest_source_version: string | null
}> => {
  const { data } = await tenantAPI.get(`/apps/public-catalog/${appId}`)
  return data
}

// ============================================================================
// Private App Auto-Update APIs
// ============================================================================

export interface TenantAppAutoUpdateSettings {
  auto_update_enabled: boolean
  update_check_interval: number
  last_update_check: string | null
  latest_source_version: string | null
  update_status: string
  source_reference: string | null
  package_source: string | null
}

export interface TenantAppAutoUpdateUpdate {
  auto_update_enabled?: boolean
  update_check_interval?: number
  source_reference?: string
}

export interface TenantAppVersion {
  id: string
  version: string
  status: string
  created_at: string
  published_at: string | null
  superseded_at: string | null
  package_storage: Record<string, unknown> | null
  test_result: string | null
  /** The version's package folder is still on disk, so it can be downloaded. */
  has_package?: boolean
  aeam_report: string | null
  source_job_id: string | null
}

/**
 * Get auto-update settings for a private (tenant) app
 * GET /apps/{app_id}/auto-update
 */
export const getTenantAppAutoUpdate = async (appId: string): Promise<TenantAppAutoUpdateSettings> => {
  const { data } = await tenantAPI.get(`/apps/${appId}/auto-update`)
  return data
}

/**
 * Update auto-update settings for a private (tenant) app
 * PATCH /apps/{app_id}/auto-update
 */
export const updateTenantAppAutoUpdate = async (
  appId: string,
  settings: TenantAppAutoUpdateUpdate
): Promise<TenantAppAutoUpdateSettings> => {
  const { data } = await tenantAPI.patch(`/apps/${appId}/auto-update`, settings)
  return data
}

/**
 * Manually check for updates for a private (tenant) app
 * POST /apps/{app_id}/check-update
 */
export const checkTenantAppUpdate = async (appId: string): Promise<{
  update_available: boolean
  current_version: string | null
  latest_version: string | null
  update_status: string
  message: string
}> => {
  const { data } = await tenantAPI.post(`/apps/${appId}/check-update`)
  return data
}

/**
 * Trigger auto-update packaging for a private (tenant) app
 * POST /apps/{app_id}/trigger-update
 */
export const triggerTenantAppUpdate = async (appId: string, targetVersion?: string): Promise<{
  success: boolean
  job_id: string | null
  message: string
}> => {
  const { data } = await tenantAPI.post(`/apps/${appId}/trigger-update`, {
    target_version: targetVersion
  })
  return data
}

/**
 * Get version history for a private (tenant) app
 * GET /apps/{app_id}/versions
 */
export const getTenantAppVersions = async (appId: string, params?: {
  limit?: number
  offset?: number
  status?: string
}): Promise<{
  versions: TenantAppVersion[]
  total: number
}> => {
  const { data } = await tenantAPI.get(`/apps/${appId}/versions`, { params })
  return data
}

/**
 * Get a specific version of a private (tenant) app
 * GET /apps/{app_id}/versions/{version_id}
 */
export const getTenantAppVersion = async (appId: string, versionId: string): Promise<TenantAppVersion> => {
  const { data } = await tenantAPI.get(`/apps/${appId}/versions/${versionId}`)
  return data
}

/**
 * Activate a specific version of a private (tenant) app
 * POST /apps/{app_id}/versions/{version_id}/activate
 */
export const activateTenantAppVersion = async (appId: string, versionId: string): Promise<{
  success: boolean
  message: string
}> => {
  const { data } = await tenantAPI.post(`/apps/${appId}/versions/${versionId}/activate`)
  return data
}

/**
 * Delete a specific version of a private (tenant) app
 * DELETE /apps/{app_id}/versions/{version_id}
 */
export const deleteTenantAppVersion = async (appId: string, versionId: string): Promise<{
  success: boolean
  message: string
}> => {
  const { data } = await tenantAPI.delete(`/apps/${appId}/versions/${versionId}`)
  return data
}

/**
 * Get tenant subscription status for warning banner
 * GET /tenant/subscription-status
 */
export interface TenantSubscriptionStatus {
  has_subscription: boolean
  plan?: string
  status?: string
  expires_at?: string
  days_until_expiry?: number | null
  grace_period_days?: number
  /** No service at all right now — suspended, out of grace, or a paused trial. */
  service_paused?: boolean
  /** Why service is paused, written for a tenant admin. */
  pause_reason?: string | null
  can_package?: boolean
  /** Free-first onboarding is on: sign-up lands on the free plan and the trial is opt-in. */
  free_first?: boolean
  free_endpoint_limit?: number
  /** This tenant can start its one free trial right now. */
  trial_available?: boolean
  /** Days the trial runs if started now: an end the admin set on the row, else the default. */
  trial_days?: number
  /** When a running trial ends; null when not on a trial. */
  trial_ends_at?: string | null
  /** The last endpoint count the compliance sweep kept, and when. Null until measured. */
  endpoints_measured?: number | null
  endpoints_measured_at?: string | null
  /** When each running grace period ends; null while that clock is not running. */
  overage_deadline?: string | null
  payment_deadline?: string | null
}

export const getTenantSubscriptionStatus = async (): Promise<TenantSubscriptionStatus> => {
  const { data } = await tenantAPI.get('/tenant/subscription-status')
  return data
}

/**
 * Billing — the annual subscription, priced per managed endpoint.
 * Tenant-admin only; the backend rejects everyone else with a 403.
 */
export interface EndpointUsage {
  /** Distinct Intune devices with one of our apps installed. */
  installed: number
  /** Windows devices Intune manages; null when Graph would not report them. */
  managed_endpoints: number | null
  /** Why the managed endpoint inventory is missing, if it is. */
  inventory_error: string | null
  /** Distinct Intune devices our apps were assigned to, installed or not. */
  targeted: number
  /** Devices registered by the Windows device agent; not billed. */
  agent_devices: number
  /** The figure compared against licensed seats. */
  billable: number
  apps_checked: number
  apps_failed: number
  intune_available: boolean
  counted_at: string | null
  errors: string[]
}

export const getEndpointUsage = async (refresh = false): Promise<EndpointUsage> => {
  const { data } = await tenantAPI.get('/billing/endpoint-usage', { params: { refresh } })
  return data
}

export type BillingInterval = 'month' | 'year'

/** One band of the graduated ladder. */
export interface PriceTier {
  /** Band name from the SKU sheet, e.g. Starter. */
  name: string
  /** Last endpoint in this band; null for the unbounded top band. */
  up_to: number | null
  /**
   * Price per endpoint in this band, in the minor currency unit. A decimal
   * string, not a number: the Scale band is 22.5 cents and has no integer form.
   */
  unit_amount_decimal: string
}

/** A term on offer and the ladder it charges. */
export interface PricePlan {
  interval: BillingInterval
  currency: string
  tiers: PriceTier[]
}

export interface BillingSummary {
  configured: boolean
  plan: string | null
  status: string | null
  endpoints_used: number
  endpoint_usage: EndpointUsage | null
  endpoints_licensed: number | null
  expires_at: string | null
  cancel_at_period_end: boolean
  has_subscription: boolean
  /** Terms on offer, each with its band table. */
  prices: PricePlan[]
  /** Endpoints the free plan covers under the rules in force. */
  free_endpoint_limit: number
  /** Smallest subscription on sale. Not derived from the free limit: it is the minimum spend. */
  min_paid_endpoints?: number
  /** Free-first onboarding is on: sign-up lands on the free plan and the trial is opt-in. */
  free_first?: boolean
  /** What the free plan includes, so the page describes it rather than assuming. */
  free_plan?: FreePlanTerms | null
  /** This tenant can start its one free trial right now. */
  trial_available?: boolean
  /** Days the trial runs if started now: an end the admin set on the row, else the default. */
  trial_days?: number
  /** The plan's packaging quota and what is left of it; null for an unbudgeted plan. */
  packaging_budget?: PackagingBudgetStatus | null
  /** The last endpoint count the compliance sweep kept on the subscription, and when. */
  endpoints_measured?: number | null
  endpoints_measured_at?: string | null
  /** Pricing band this tenant sits in — 'Free' or a band name like Starter. */
  tier: string | null
  /** Endpoints the tenant may use before going over their plan; null on trial. */
  endpoints_entitled: number | null
  /** Whether this tenant's plan includes packaging. */
  can_package: boolean
  /** Why a service is unavailable, safe to show a tenant admin. */
  entitlement_reason: string | null
  /** When the payment grace runs out; null while paid up. */
  payment_deadline: string | null
  /** When the overage grace runs out; null while within plan. */
  overage_deadline: string | null
  /** The earliest running deadline — when service actually stops. */
  service_stops_at: string | null
  /** Term of the current subscription, if there is one. */
  interval: BillingInterval | null
  /** What the licensed quantity costs on its term, in the minor currency unit. */
  amount_total: number | null
  currency: string | null
  error: string | null
}

export interface FreePlanTerms {
  endpoint_limit: number
  includes_packaging: boolean
  packaging_apps_per_month: number | null
  packaging_jobs_per_month: number | null
  includes_managed_tenants: boolean
}

/** What is left of a budgeted plan's packaging — the trial's caps or the free plan's month. */
export interface PackagingBudgetStatus {
  apps_used: number
  apps_limit: number
  jobs_used: number
  jobs_limit: number
  /** When the counts start over; the trial's end for a trial. */
  resets_at: string | null
}

/** One band's share of a graduated total: how many endpoints it prices and at what. */
export interface TierCharge {
  /** Band name from the SKU sheet, e.g. Starter. */
  name: string
  /** Endpoints this band prices for the given quantity. */
  units: number
  /** The band's per-endpoint rate, in the minor currency unit. */
  unit_amount_decimal: string
  /**
   * units × rate, in the minor currency unit. Not rounded: the Scale band is
   * 22.5 cents, so an odd count there lands on a half cent.
   */
  subtotal: number
}

/**
 * The per-band lines behind `graduatedTotal`, for showing the calculation.
 *
 * Each band's rate applies only to the endpoints inside it, so a quantity is
 * charged as a sum of bands and not one rate times the quantity.
 */
export const graduatedBreakdown = (
  tiers: PriceTier[],
  quantity: number,
): TierCharge[] | null => {
  if (quantity <= 0) return []
  if (tiers.length === 0) return null

  const lines: TierCharge[] = []
  let priced = 0
  for (const tier of tiers) {
    // The final band is unbounded and reports no ceiling.
    const ceiling = tier.up_to === null ? quantity : Math.min(tier.up_to, quantity)
    const units = ceiling - priced
    if (units <= 0) continue

    const rate = Number(tier.unit_amount_decimal)
    if (!Number.isFinite(rate)) return null
    lines.push({
      name: tier.name,
      units,
      unit_amount_decimal: tier.unit_amount_decimal,
      subtotal: rate * units,
    })

    priced = ceiling
    if (priced >= quantity) break
  }
  return lines
}

/**
 * What `quantity` endpoints cost on a ladder, in the minor currency unit.
 *
 * Computed here rather than fetched so the figure tracks the stepper without a
 * round trip; the backend runs the same sum over the same tiers, and Stripe's
 * invoice is authoritative.
 */
export const graduatedTotal = (tiers: PriceTier[], quantity: number): number | null => {
  const lines = graduatedBreakdown(tiers, quantity)
  if (lines === null) return null
  return Math.round(lines.reduce((sum, line) => sum + line.subtotal, 0))
}

export const getBillingSummary = async (): Promise<BillingSummary> => {
  const { data } = await tenantAPI.get('/billing/summary')
  return data
}

/** Returns the Stripe Checkout URL to redirect to. */
export const createBillingCheckoutSession = async (
  quantity: number,
  interval: BillingInterval = 'year',
): Promise<string> => {
  const { data } = await tenantAPI.post('/billing/checkout-session', { quantity, interval })
  return data.url
}

/** Starts the tenant's one free trial from the free plan; answers the updated summary. */
export const startTrial = async (): Promise<BillingSummary> => {
  const { data } = await tenantAPI.post('/billing/trial')
  return data
}

/** Returns the Stripe Customer Portal URL to redirect to. */
export const createBillingPortalSession = async (): Promise<string> => {
  const { data } = await tenantAPI.post('/billing/portal-session')
  return data.url
}

// ---------------------------------------------------------------------------
// Branding templates and branding deployments
//
// A branding template is authored appearance; a branding DEPLOYMENT is that appearance frozen and
// shipped to a set of groups. They are separate here because they have different lifetimes — a
// template is edited, a deployment is a thing that happened — and editing the first must not appear
// to change the second.
// ---------------------------------------------------------------------------
export interface BrandingTemplate {
  id: string
  name: string
  description?: string | null
  tenant_id?: string | null
  read_only: boolean
  company_name?: string | null
  /** PSADT's spelling, as the column stores it: 'Fluent' | 'Classic'. */
  dialog_style?: string | null
  accent_color?: string | null
  language_override?: string | null
  /** The welcome dialog's wording. Ships as a PSADT string table beside the configuration, so it
   *  overrides only the string it names and leaves every other one in every shipped language. */
  welcome_custom_text?: string | null
  logo_asset_id?: string | null
  banner_asset_id?: string | null
  /** WHO receives it. Held on the branding so Deploy is a single action: by the time it is pressed
   *  the audience is already known. null means nobody has chosen one yet, which is why Deploy is
   *  refused rather than publishing an application that reaches no device. */
  assignments?: AssignmentCreate[] | null
  /** What an author can act on: building | ready | failed. Not the signing column's vocabulary. */
  state: 'building' | 'ready' | 'failed'
  deployable: boolean
  /** What this branding takes over on a device that holds it, in the author's words. */
  settings: string[]
  identity?: string | null
  error?: string | null
  generated_at?: string | null
  signed_at?: string | null
}

export interface BrandingDeployment {
  id: string
  name: string
  branding_template_id?: string | null
  identity: string
  group_ids: string[]
  /** The AUTHORED set this shipped with. Null for a deployment recorded before one could be
   *  authored, which means Required to each of group_ids. It is the only place a non-group audience
   *  appears: All Devices and All Users name no group at all. */
  assignments?: AssignmentCreate[] | null
  intune_app_id?: string | null
  /** `withdrawing` = assigned to UNINSTALL: still in Intune, no longer branding anything. It is
   *  deliberately not "live", so it covers no device — and it is the only state from which
   *  removing the record strands nothing. */
  status: 'publishing' | 'published' | 'failed' | 'withdrawing'
  error?: string | null
  settings: string[]
  company_name?: string | null
  dialog_style?: string | null
  published_at?: string | null
  created_at?: string | null
}

export const listBrandingTemplates = async (): Promise<{ templates: BrandingTemplate[] }> => {
  const { data } = await tenantAPI.get('/branding/templates')
  return data
}

export const createBrandingTemplate = async (body: {
  name: string
  description?: string
  company_name?: string | null
  dialog_style?: string | null
  accent_color?: string | null
  language_override?: string | null
}): Promise<BrandingTemplate> => {
  const { data } = await tenantAPI.post('/branding/templates', body)
  return data
}

export const updateBrandingTemplate = async (
  id: string,
  body: {
    name: string
    description?: string
    company_name?: string | null
    dialog_style?: string | null
    accent_color?: string | null
    language_override?: string | null
    /** The PUT replaces every column from the body, so an edit that omits these clears the
     *  branding's logo and banner. Send the template's current ids to keep its images. */
    logo_asset_id?: string | null
    banner_asset_id?: string | null
  },
): Promise<BrandingTemplate> => {
  const { data } = await tenantAPI.put(`/branding/templates/${id}`, body)
  return data
}

export const deleteBrandingTemplate = async (id: string): Promise<void> => {
  await tenantAPI.delete(`/branding/templates/${id}`)
}

/** Build this branding again from what is already saved, changing nothing about it.
 *
 *  A build can fail for reasons unrelated to the branding — the signer unreachable, a worker
 *  restarted — and those clear on their own. Without this the only way to try again is to open the
 *  editor and save an edit you do not want. */
export const rebuildBrandingTemplate = async (id: string): Promise<BrandingTemplate> => {
  const { data } = await tenantAPI.post(`/branding/templates/${id}/rebuild`)
  return data
}

export const listBrandingDeployments = async (): Promise<{ deployments: BrandingDeployment[] }> => {
  const { data } = await tenantAPI.get('/branding/deployments')
  return data
}

/** ONE branding deployment, plus how it actually landed on each device.
 *
 *  The install status is the same read the application deployment page makes, against this
 *  branding's own Intune application, so the two pages cannot disagree about a device. */
/** One target of a branding deployment's assignment, resolved for reading.
 *
 *  `name` is null for a tenant-wide target, which names no group, and also for a group Entra would
 *  not name — the assignment still points at that group, and dropping it would hide that the
 *  branding is assigned to something that no longer exists. */
export interface AudienceTarget {
  type: 'group' | 'allDevices' | 'allUsers'
  id: string | null
  name: string | null
  intent: string
  include: boolean
  /** How many managed devices the target holds right now, or null when nobody could ask. */
  device_count?: number | null
}

export interface BrandingDeploymentDetails extends BrandingDeployment {
  /** Null when Intune could not be asked — which is NOT an empty estate. `intune_error` says why. */
  install_stats?: {
    installed: number
    pending: number
    failed: number
    notApplicable: number
    /** Devices in the audience Intune has reported nothing about. Absent when the audience could
     *  not be resolved: a count of machines nobody could enumerate would read as zero whatever the
     *  truth. */
    notReported?: number
  } | null
  /** Every device in the audience, not only the ones that reported — a machine assigned this
   *  branding that Intune has said nothing about appears in no report. */
  device_statuses?: DeviceInstallStatus[] | null
  intune_error?: string | null
  /** WHO IT REACHES, resolved from CURRENT group membership on read. `device_count` is null when
   *  the groups could not be expanded, which `audience_error` explains. */
  audience?: { targets: AudienceTarget[]; device_count: number | null } | null
  audience_error?: string | null
  /** The template this was authored from, or null once that template has been edited away. */
  authored_from?: { id: string; name: string; identity?: string | null } | null
}

export const getBrandingDeployment = async (id: string): Promise<BrandingDeploymentDetails> => {
  const { data } = await tenantAPI.get(`/branding/deployments/${id}`)
  return data
}

/** Forget the record. THIS DOES NOT UN-BRAND ANYTHING.
 *
 *  The Intune application and its assignments stay, so devices keep the branding and new devices
 *  still receive it. The API refuses this while the deployment is still live, precisely because a
 *  deleted record leaves an application nothing here can remove afterwards. Uninstall first. */
export const removeBrandingDeployment = async (id: string): Promise<void> => {
  await tenantAPI.delete(`/branding/deployments/${id}`)
}

/** Take the branding OFF the devices that hold it.
 *
 *  The only action that does. It flips the Intune assignment to the uninstall intent, which runs
 *  the package's own Uninstall.ps1 on each device and restores the packaged branding — so the
 *  application is KEPT, because it is the thing doing the removing. */
/** Uninstall this branding from its audience AND delete the application from Intune.
 *
 *  Ordered on the server: the uninstall assignment is written first and the delete only happens if
 *  it succeeded, because the application is the only thing that removes a branding from a device. A
 *  machine that has not checked in by then keeps the branding permanently — which is why Uninstall
 *  exists separately, keeping the application so stragglers still receive it. */
export const removeBrandingFromIntune = async (deploymentId: string): Promise<any> => {
  const { data } = await tenantAPI.post(`/branding/deployments/${deploymentId}/remove`)
  return data
}

export const uninstallBrandingDeployment = async (id: string): Promise<BrandingDeployment> => {
  const { data } = await tenantAPI.post(`/branding/deployments/${id}/uninstall`)
  return data
}

/** Change WHO a published branding reaches, without changing what it is.
 *
 *  The deployment's frozen copy of what shipped is untouched; only the audience moves. */
export const reassignBrandingDeployment = async (
  id: string,
  body: { group_ids?: string[]; assignments?: AssignmentCreate[] },
): Promise<BrandingDeployment> => {
  const { data } = await tenantAPI.put(`/branding/deployments/${id}/assignments`, body)
  return data
}

/** Say who a branding is for. Its OWN endpoint, not part of the template PUT: that one returns the
 *  branding to `building` and rebuilds it, and an audience is not among the bytes the signature
 *  covers — so routing it through there would unsign a branding every time a group was added. */
export const setBrandingTemplateAssignments = async (
  templateId: string,
  assignments: AssignmentCreate[],
): Promise<BrandingTemplate> => {
  const { data } = await tenantAPI.put(`/branding/templates/${templateId}/assignments`, {
    assignments,
  })
  return data
}

export const deployBranding = async (body: {
  template_id: string
  /** Only for the simple case. With `assignments` present the server DERIVES this from it, so
   *  sending both is how coverage comes to disagree with what is actually assigned. */
  group_ids?: string[]
  /** The authored set: Required and Uninstall only — a branding is never `available`, since
   *  nobody browses to a re-skin in Company Portal. */
  assignments?: AssignmentCreate[]
  /** The Intune deployment template this publishes under — the SAME object an application deploy
   *  names, resolved by the same resolver. Alternatives, never both: the id when a template was
   *  chosen as-is, the config when Adjust edited one and there is no row behind it. */
  intune_template_id?: string
  intune_inline_config?: any
  /** REPLACE the deployment this branding already has. Without it the server refuses a second copy
   *  with 409, which is the right default — one branding is one deployment, and two applications
   *  carrying it would share a detection rule. Set only from a prompt naming what will be replaced. */
  force?: boolean
}): Promise<BrandingDeployment> => {
  const { data } = await tenantAPI.post('/branding/deployments', body)
  return data
}

/** Upload an image into one of a branding's PSADT asset slots.
 *
 *  The slot is explicit because which one renders depends on the dialog style: Fluent draws
 *  Assets.Logo, Classic draws Assets.Banner. An image in the wrong slot is present, valid and never
 *  drawn — the failure that is hardest to notice, because nothing reports it. */
export const uploadBrandingImage = async (
  templateId: string,
  slot: 'logo' | 'banner',
  file: File,
): Promise<BrandingTemplate> => {
  const form = new FormData()
  form.append('file', file)
  const { data } = await tenantAPI.post(
    `/branding/templates/${templateId}/image?slot=${slot}`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

export const removeBrandingImage = async (
  templateId: string,
  slot: 'logo' | 'banner',
): Promise<void> => {
  await tenantAPI.delete(`/branding/templates/${templateId}/image?slot=${slot}`)
}

/** The branding package itself, as its contents or as the file Intune was given.
 *
 *  `zip` is the one to reach for: it holds the signed config.psd1, which carries the asset paths and
 *  the identity, so it can be compared against %ProgramData%\sap\psadt\config on a device that is
 *  not showing the branding it was sent. That failure reports as a successful install, because the
 *  on-device gate ignores a configuration it will not accept rather than failing.
 *
 *  `intunewin` is the uploaded file. Its payload is encrypted, so it proves what was sent rather
 *  than being something to read. */
export const downloadBrandingPackage = async (
  templateId: string,
  artifact: 'zip' | 'intunewin',
): Promise<Blob> => {
  const { data } = await tenantAPI.get(`/branding/templates/${templateId}/package`, {
    params: { artifact },
    responseType: 'blob',
  })
  return data
}

/** A branding's stored image, for preview.
 *
 *  Fetched through the API client and shown as an object URL rather than pointed at with
 *  `<img src="/api/…">`: the route authenticates with the bearer token this client attaches, and a
 *  browser image request carries no Authorization header, so the element got 401 and drew nothing. */
export const fetchBrandingImage = async (
  templateId: string,
  slot: 'logo' | 'banner',
): Promise<Blob> => {
  const { data } = await tenantAPI.get(`/branding/templates/${templateId}/image`, {
    params: { slot },
    responseType: 'blob',
  })
  return data
}

// ---------------------------------------------------------------------------
// App bundles — a named set of install/uninstall actions run by the agent CLI
// ---------------------------------------------------------------------------

/** What a bundle does with one app on the machine. install: install where absent, update where
 *  older, skip where current. update: update where present and older, nothing where absent. */
export type BundleItemAction = 'install' | 'update' | 'uninstall'

export const BUNDLE_ACTION_LABEL: Record<BundleItemAction, string> = {
  install: 'Install or update',
  update: 'Update only',
  uninstall: 'Uninstall',
}

export interface BundleApp {
  id: string
  app_id: string
  app_name: string
  action: BundleItemAction
  order_index: number
}

export interface Bundle {
  id: string
  name: string
  description: string | null
  apps: BundleApp[]
  item_count: number
  install_count: number
  update_count: number
  uninstall_count: number
  created_by: string | null
  created_at: string | null
  updated_at: string | null
  /** The one-liner for the bundle's newest token that still runs, or null when there is none. */
  cli_command: string | null
  command_expires_at: string | null
  /** The UX template this bundle deploys with; null means it installs silently. */
  ux_template_id: string | null
  ux_template_name: string | null
  /** Only what this bundle changes about that template. The rest keeps following it. */
  ux_overrides_json: any | null
  /** Template + adjustments, already merged — what the machine will actually see. */
  ux_config: any | null
}

export const listBundles = async (): Promise<{ bundles: Bundle[] }> => {
  const { data } = await tenantAPI.get('/bundles')
  return { bundles: data.bundles || [] }
}

export const createBundle = async (body: {
  name: string
  description?: string | null
  apps?: { app_id: string; action: BundleItemAction }[]
  ux_template_id?: string | null
  ux_overrides_json?: any | null
}): Promise<{ id: string; name: string }> => {
  const { data } = await tenantAPI.post('/bundles', body)
  return data
}

/**
 * Update a bundle. Only the fields present are touched — sending `ux_template_id: null` CLEARS the
 * selection (and its adjustments with it), while leaving the key out keeps whatever is there.
 */
export const updateBundle = async (bundleId: string, body: {
  name?: string
  description?: string | null
  ux_template_id?: string | null
  ux_overrides_json?: any | null
}): Promise<{ message: string }> => {
  const { data } = await tenantAPI.put(`/bundles/${bundleId}`, body)
  return data
}

export const deleteBundle = async (bundleId: string): Promise<void> => {
  await tenantAPI.delete(`/bundles/${bundleId}`)
}

/** Mints a run token and returns the commands it makes valid. */
export const generateBundleToken = async (
  bundleId: string,
  body: { expiry_hours: number; max_uses: number | null },
): Promise<{ token: string; expires_at: string; cli_command: string; oneliner: string }> => {
  const { data } = await tenantAPI.post(`/bundles/${bundleId}/generate-token`, body)
  return data
}
